/**
 * webcam-recorder/recorder.js — etalon webcam-capture pipeline.
 *
 * Adapted from screen-recorder/recorder.js: everything below acquisition is
 * source-agnostic, so the WebCodecs/MediaRecorder/mixer machinery is kept
 * verbatim. The difference is the source — a camera MediaStream is injected
 * via opts.acquire() (the blade supplies { videoStream, micStream }) instead
 * of getDisplayMedia + sys-audio. The audio mixer is mic-only here.
 *
 * Tier-1 path (modern Chromium / Edge / YaBrowser ≥ Chromium 94):
 *   camera stream → MediaStreamTrackProcessor → VideoEncoder (H.264) →
 *     mp4-muxer (fastStart='in-memory', moov-at-start) → ArrayBufferTarget → Blob
 *   Audio: AudioContext mix → MediaStreamAudioDestinationNode →
 *     MediaStreamTrackProcessor → AudioEncoder (AAC-LC) → mp4-muxer.
 *
 *   Output is correct by construction: moov at start, accurate Duration,
 *   sample-accurate audio sync, no post-processing. Plays in every player.
 *
 * Tier-2 path (Firefox, Safari, old Chromium):
 *   MediaRecorder. On Chromium/Firefox → WebM/Opus + WebmFixDuration post-stop
 *   EBML rewrite (injects Segment Duration so desktop players show correct
 *   length). On Safari/iOS → native H.264 MP4 (clean & in-order for CAMERA
 *   input, unlike fragmented screen-capture MP4 — so MP4 is preferred there).
 *
 * Why two tiers:
 *   - WebCodecs is universally faster, produces clean files, supports
 *     real CRF-style bitrate control. But MediaStreamTrackProcessor is
 *     Chromium-only as of Chromium 126 (Yandex Browser 24.x). FF/Safari
 *     get the fallback.
 *   - MediaRecorder is universally available. WebM from MediaRecorder needs
 *     the WebmFixDuration patch; camera-MP4 from Safari is clean as-is.
 *
 * Public surface:
 *   var rec = new WebcamRecorder({
 *       quality: 'medium',                    // 'lossless'|'high'|'medium'|'compact'
 *       fps: 30,
 *       captureMic: true,
 *       micVolume: 1.0,
 *       acquire: function(){ return Promise.resolve({ videoStream, micStream }); },
 *       onState: function(s){...},            // 'starting'|'recording'|'paused'|'finalizing'|'done'|'error'
 *       onProgress: function(stats){...},     // {framesEncoded, audioFrames, sizeBytesEstimate, queueDepth}
 *       onError: function(err){...},
 *       onCameraError: function(err){...},    // typed camera-acquire failure
 *       onAudioMissing: function(info){...},  // returns 'continue'|'cancel'
 *       telemetry: function(name, payload){...} // hook for __ae
 *   });
 *   await rec.start();          // calls opts.acquire() (blade owns the prompts)
 *   rec.pause();
 *   rec.resume();
 *   var result = await rec.stop();
 *     // → { blob, mime, ext, durationMs, sizeBytes, pipeline:'webcodecs'|'mediarecorder', codec, container }
 *   rec.cancel();               // abort, no output
 *   rec.getCapabilities();      // for UI hints
 */
(function () {
'use strict';
if (window.WebcamRecorder) return;
var TOOL_SLUG = 'webcam-recorder';

// ─────────────────────────────────────────────────────────────────────
// CAPABILITY DETECTION
// ─────────────────────────────────────────────────────────────────────

function hasGetDisplayMedia() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia);
}

function hasWebCodecsLive() {
    // MediaStreamTrackProcessor is the bridge between MediaStream and
    // WebCodecs. Without it, we cannot do live capture via WebCodecs and
    // must fall through to MediaRecorder.
    return typeof window.VideoEncoder !== 'undefined'
        && typeof window.AudioEncoder !== 'undefined'
        && typeof window.MediaStreamTrackProcessor !== 'undefined';
}

function hasMediaRecorder() {
    return typeof window.MediaRecorder !== 'undefined';
}

function hasFileSystemAccess() {
    return typeof window.showSaveFilePicker === 'function';
}

async function checkVideoEncoderConfig(codec, width, height, bitrate, framerate) {
    if (typeof VideoEncoder === 'undefined') return null;
    try {
        var hw = await VideoEncoder.isConfigSupported({
            codec: codec, width: width, height: height,
            bitrate: bitrate, framerate: framerate,
            hardwareAcceleration: 'prefer-hardware'
        });
        if (hw && hw.supported) return { supported: true, hardware: true, config: hw.config };
    } catch (_) {}
    try {
        var sw = await VideoEncoder.isConfigSupported({
            codec: codec, width: width, height: height,
            bitrate: bitrate, framerate: framerate
        });
        if (sw && sw.supported) return { supported: true, hardware: false, config: sw.config };
    } catch (_) {}
    return { supported: false, hardware: false };
}

async function checkAudioEncoderConfig(codec, sampleRate, channels, bitrate) {
    if (typeof AudioEncoder === 'undefined') return null;
    try {
        var r = await AudioEncoder.isConfigSupported({
            codec: codec, sampleRate: sampleRate,
            numberOfChannels: channels, bitrate: bitrate
        });
        return { supported: !!(r && r.supported), config: r ? r.config : null };
    } catch (_) {
        return { supported: false };
    }
}

// ─────────────────────────────────────────────────────────────────────
// QUALITY → BITRATE MAP
//
// We give users 4 named tiers, mapped to per-pixel bit budgets that
// scale with output resolution × framerate. This is closer in spirit to
// CRF than the previous "8 Mbit/s vs 5 Mbit/s" fixed values, because
// 8 Mbit/s is great for 720p but starves 1440p. Multipliers come from
// Google's recommended H.264 ranges (https://support.google.com/youtube/answer/1722171)
// and are rounded slightly conservative for screen content (which has
// low entropy and compresses better than camera footage).
// ─────────────────────────────────────────────────────────────────────

var QUALITY_BITS_PER_PIXEL_PER_SEC = {
    lossless: 0.30,   // ~62 Mbps for 1080p30 — visually lossless H.264
    high:     0.08,   // ~16 Mbps for 1080p30 — broadcast quality
    medium:   0.05,   // ~10 Mbps for 1080p30 — clear and small
    compact:  0.02    //  ~4 Mbps for 1080p30 — small files, watchable
};

function bitrateFor(quality, width, height, fps) {
    var bpp = QUALITY_BITS_PER_PIXEL_PER_SEC[quality] || QUALITY_BITS_PER_PIXEL_PER_SEC.medium;
    var b = Math.round(width * height * fps * bpp);
    // Floor and ceiling — useful for ultra-tiny tabs and 4K screens.
    return Math.max(500_000, Math.min(80_000_000, b));
}

function audioBitrateFor(quality) {
    if (quality === 'lossless' || quality === 'high') return 192_000;
    if (quality === 'medium') return 128_000;
    return 96_000;
}

// The AAC AudioSpecificConfig (esds descriptor) and the ADTS-vs-raw framing of
// every AAC chunk are owned by AacAccessUnit (public/js/aac-access-unit.js),
// loaded by the blade next to mp4-muxer — the recorder used to carry its own
// ASC builder «to avoid coupling», which is how four copies came to exist and
// none of them looked at the bytes (report #6990).

// ─────────────────────────────────────────────────────────────────────
// AUDIO MIXER
//
// AudioContext + DynamicsCompressor + per-source gain. Mic and system
// audio go through individual GainNodes (so the volume sliders stay
// real-time) and merge into a compressor that prevents clipping when
// both peak. Output: a MediaStreamAudioDestinationNode whose track is
// the encoder source.
// ─────────────────────────────────────────────────────────────────────

function buildMixer(displayStream, micStream, opts) {
    var displayAudio = displayStream ? displayStream.getAudioTracks() : [];
    if (displayAudio.length === 0 && !micStream) return null;

    var ctx = new AudioContext();
    var dest = ctx.createMediaStreamDestination();

    var compressor = ctx.createDynamicsCompressor();
    // Gentle, voice-tuned safety limiter (vs screen-recorder's aggressive
    // -18 / 12 / 4:1 tuned for loud, dynamic system audio). A single speaking
    // voice through the loud screen-audio settings pumps/ducks audibly, so we
    // soften to a near-transparent peak limiter that only catches clipping.
    compressor.threshold.value = -3;
    compressor.knee.value = 3;
    compressor.ratio.value = 2;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.25;
    compressor.connect(dest);

    var sysGain = null, micGain = null;
    if (displayAudio.length > 0) {
        sysGain = ctx.createGain();
        sysGain.gain.value = opts.sysVolume != null ? opts.sysVolume : 1.0;
        ctx.createMediaStreamSource(new MediaStream(displayAudio)).connect(sysGain);
        sysGain.connect(compressor);
    }
    if (micStream) {
        micGain = ctx.createGain();
        micGain.gain.value = opts.micVolume != null ? opts.micVolume : 1.0;
        ctx.createMediaStreamSource(micStream).connect(micGain);
        micGain.connect(compressor);
    }

    // Silence detector — taps the compressed bus PRE-destination so it
    // sees what's actually going to the encoder. Closes Дмитрий #738
    // (2026-06-13, "с 35 секунды пропал звук") where the audio track
    // stayed alive and the encoder kept encoding, but the source went
    // silent (tab video ended / share-tab-audio toggled off mid-recording
    // / OS audio session suspended). No encoder error, no track ended,
    // so nothing in telemetry — pure silent regression of an active
    // recording. Now: poll RMS, after 5 s of < -50 dBFS while we
    // STARTED with real audio, flag _audioSilenceMid.
    var analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.3;
    compressor.connect(analyser);

    return { ctx: ctx, dest: dest, sysGain: sysGain, micGain: micGain, analyser: analyser };
}

// ─────────────────────────────────────────────────────────────────────
// STOP WAITS — shared with the screen recorder (js/screen-recorder/
// recorder-stop.js: why every wait of a Stop is bounded, and how a
// MediaRecorder stop settles). If that file did not load, a plain timer still
// bounds the waits — a hidden tab may stretch it, it cannot make it endless.
// ─────────────────────────────────────────────────────────────────────
function bounded(p, ms) {
    var RS = window.RecorderStop;
    if (RS && typeof RS.bounded === 'function') return RS.bounded(p, ms);
    return new Promise(function (resolve) {
        var id = setTimeout(function () { resolve({ ok: false, timedOut: true }); }, ms);
        Promise.resolve(p).then(function (v) { clearTimeout(id); resolve({ ok: true, value: v }); },
                                function (e) { clearTimeout(id); resolve({ ok: false, error: e }); });
    });
}
// → 'stop' | 'error' | 'inactive' | 'stop-throw' | 'watchdog'
function settleMr(mr, watchdogMs) {
    var RS = window.RecorderStop;
    if (RS && typeof RS.settleMediaRecorder === 'function') return RS.settleMediaRecorder(mr, { watchdogMs: watchdogMs });
    return new Promise(function (resolve) {
        var done = false;
        function fin(via) { if (!done) { done = true; resolve(via); } }
        mr.onstop = function () { fin('stop'); };
        mr.onerror = function () { fin('error'); };
        setTimeout(function () { fin('watchdog'); }, watchdogMs);
        if (mr.state === 'inactive') { setTimeout(function () { fin('inactive'); }, 50); return; }
        try { mr.stop(); } catch (_) { fin('stop-throw'); }
    });
}

// ─────────────────────────────────────────────────────────────────────
// WEBCODECS PIPELINE
// ─────────────────────────────────────────────────────────────────────

// Probe storage quota — used by the orchestrator to (a) refuse to start
// when free space is critically low (< 500 MB), (b) warn the user during
// recording when usage crosses 80% of quota, (c) decide to gracefully
// auto-stop on QuotaExceededError. /join uses an analogous check
// (opfsCheckQuota) but with a static 1.5× headroom; for live recording
// the duration is unknown so we use percentage-of-quota instead.
function probeStorageQuota() {
    /* The REAL grant (window.__storageEstimate, timbrica-core.js). Until
       2026-09-25 this read navigator.storage.estimate(), which on most Chromium
       builds answers usage + 10 GiB whatever is really left — every guard below
       (the low-space warning, the pre-flight eviction, the stop before the disk
       is full) was blind on the most common browser, and a nearly full disk
       showed up only as a write refused mid-take. */
    if (typeof window.__storageEstimate !== 'function') {
        return Promise.resolve({ supported: false, free: Infinity, usage: 0, quota: Infinity });
    }
    return window.__storageEstimate().then(function (est) {
        if (!est) return { supported: false, free: Infinity, usage: 0, quota: Infinity };
        var quota = est.quota || 0;
        var usage = est.usage || 0;
        return {
            supported: true,
            free: Math.max(0, quota - usage),
            usage: usage,
            quota: quota,
            usagePct: quota > 0 ? usage / quota : 0,
            measured: !!est.measured
        };
    }).catch(function () {
        return { supported: false, free: Infinity, usage: 0, quota: Infinity };
    });
}

// Defensive sweep at module init: ONLY deletes OPFS files that:
//   (a) are older than the 5-min safety window (anti-Ирина #539: don't
//       kill the file backing an in-flight Blob download), AND
//   (b) are NOT registered in RecordingsHistory IDB (the user's library).
//
// Files registered in history are protected indefinitely — they're the
// user's saved recordings. Auto-eviction is handled by
// RecordingsHistory.autoEvictIfNeeded() driven by quota pressure, not
// by this sweep. Orphans (OPFS files with no IDB entry, e.g. crashed
// finalize) still get cleaned up via the age check.
var SWEEP_AGE_THRESHOLD_MS = 5 * 60 * 1000;
function sweepStaleOpfsRecordings() {
    if (typeof navigator.storage === 'undefined' ||
        typeof navigator.storage.getDirectory !== 'function') return Promise.resolve();
    return navigator.storage.getDirectory().then(function (root) {
        return (async function () {
            try {
                var now = Date.now();
                // Build the "protected" set: any file the user has in their
                // history library is off-limits regardless of age.
                var protectedNames = {};
                if (window.RecordingsHistory && typeof window.RecordingsHistory.list === 'function') {
                    try {
                        var rows = await window.RecordingsHistory.list();
                        for (var i = 0; i < rows.length; i++) protectedNames[rows[i].fileName] = true;
                    } catch (_) {}
                }
                for await (var entry of root.values()) {
                    if (entry.kind !== 'file') continue;
                    if (entry.name.indexOf('_' + TOOL_SLUG + '_') !== 0) continue;
                    if (/\.crswap$/.test(entry.name)) continue;
                    if (protectedNames[entry.name]) continue; // user's library, keep
                    // Filename embeds the creation timestamp:
                    //   _webcam-recorder_<Date.now()>_<rand>.mp4
                    var m = entry.name.match(new RegExp('_' + TOOL_SLUG + '_(\\d+)_'));
                    var fileTs = m ? parseInt(m[1], 10) : 0;
                    if (!fileTs || (now - fileTs) < SWEEP_AGE_THRESHOLD_MS) {
                        continue; // fresh — possible in-flight download
                    }
                    try { await root.removeEntry(entry.name); } catch (_) {}
                }
            } catch (_) {}
        })();
    }).catch(function () {});
}

// Run once at script load. Safe even if no recording is ever made.
try { sweepStaleOpfsRecordings(); } catch (_) {}

// ─────────────────────────────────────────────────────────────────────
// SHARED AUDIO-SILENCE MONITORS (used by BOTH pipelines)
//
// Module-scope so newWebCodecsPipeline AND newMediaRecorderPipeline call
// the SAME implementation. Pre-2026-06-13b these monitors lived inside
// newWebCodecsPipeline → the MediaRecorder fallback (Firefox / Safari /
// Yandex-cascade / no-h264 Chromium = ~10-20% of users) had ZERO silence
// detection, so Дмитрий #738 stayed open for exactly the cohort the
// fallback exists to serve. Hoisting closes that gap.
//
// Delegates the RMS+peak loop + AudioContext watch to the shared
// RecordingUtils primitive (public/js/recording-utils.js) so dictaphone /
// future recorders can adopt the identical detector. This file only wires
// the orchestrator's flags (_audioSilenceMid / _audioContextSuspended),
// telemetry, and the live onWarning UX.
//
// `isRunning` is a pipeline-specific predicate:
//   WebCodecs: () => encoderRunning && !aborted
//   MediaRecorder: () => mr && mr.state !== 'inactive'
function attachSilenceMonitors(self, isRunning) {
    var mx = self._mixer;
    if (!mx || !mx.analyser) return;
    if (!window.RecordingUtils ||
        typeof window.RecordingUtils.attachSilenceMonitor !== 'function') {
        // RecordingUtils not loaded — degrade gracefully (no silence
        // detection) rather than throw. The post-stop banners for the
        // OTHER classes (encoder error, quota) still work.
        self._tel('record_silence_monitor_unavailable', {});
        return;
    }
    // Tear down any prior monitor (defensive — start() should only run once
    // per recording, but a re-entry would otherwise leak a polling loop).
    if (self._silenceMon) { try { self._silenceMon.stop(); } catch (_) {} }
    if (self._ctxMon) { try { self._ctxMon.stop(); } catch (_) {} }

    self._silenceMon = window.RecordingUtils.attachSilenceMonitor(TOOL_SLUG, mx, {
        userWantedAudio: !!self._userWantedAudio,
        isRunning: isRunning,
        isPaused: function () { return self._state === 'paused'; },
        getContext: function () { return mx.ctx; },
        getTracks: function () {
            var t = [];
            try { if (self._displayStream) t = t.concat(self._displayStream.getAudioTracks()); } catch (_) {}
            try { if (self._micStream) t = t.concat(self._micStream.getAudioTracks()); } catch (_) {}
            return t;
        },
        onSilenceConfirmed: function (payload) {
            silenceDispatch(self, payload);
        },
        onRecover: function () {
            // Signal returned — clear the live banner. Sticky result flag
            // stays true (the recording DID have a silent stretch, the
            // post-stop banner should still note it).
            if (self._opts.onWarning) {
                try { self._opts.onWarning('audio_silence_recovered', {}); } catch (_) {}
            }
        }
    });

    self._ctxMon = window.RecordingUtils.attachAudioCtxMonitor(TOOL_SLUG, mx.ctx, {
        isRunning: isRunning,
        isActive: function () { return self._state === 'recording' || self._state === 'paused'; },
        onResumeFail: function () { self._audioContextSuspended = true; },
        onTelemetry: function (name, payload) { self._tel(name, payload); }
    });
}

// Single dispatch point for "silence confirmed" — called by BOTH the
// RMS-detection path (RecordingUtils monitor) and the track-event path
// (mute listeners in the orchestrator). Factored out so both routes set
// the same flag, fire the same telemetry, and raise the same LIVE warning
// (the actual Дмитрий fix — he recorded 3h not knowing audio died at 35s;
// the live warning lets him stop after seconds, not hours).
function silenceDispatch(self, payload) {
    if (self._audioSilenceMid) return; // once-only, matches quota_low/critical
    self._audioSilenceMid = true;
    self._tel('record_audio_silence_mid', payload || {});
    if (self._opts.onWarning) {
        try { self._opts.onWarning('audio_silence_mid', payload || {}); } catch (_) {}
    }
}

// Count total live audio tracks across display + mic streams. A per-track
// 'mute' event means the recording is silent ONLY when that track is the
// sole audio source — if the user has BOTH mic and sys audio and only the
// mic mutes, the mix still has sound (the RMS monitor, which taps the mixed
// bus, is authoritative for that case). So a track-mute fast-path may only
// raise the user-facing silence warning when it's the lone source;
// otherwise it just emits telemetry and defers to the RMS monitor.
function liveAudioTrackCount(self) {
    var n = 0;
    try { if (self._displayStream) n += self._displayStream.getAudioTracks().filter(function (t) { return t.readyState === 'live'; }).length; } catch (_) {}
    try { if (self._micStream) n += self._micStream.getAudioTracks().filter(function (t) { return t.readyState === 'live'; }).length; } catch (_) {}
    return n;
}

// Track-mute handler: dispatch the live warning only if this is the sole
// audio source; otherwise log telemetry and let the RMS monitor decide
// (it sees the mixed output, so it won't false-fire while another source
// is still audible).
function onTrackMute(self, source) {
    if (self._state !== 'recording' && self._state !== 'paused') return;
    var durMs = self._timerStart ? Math.round(performance.now() - self._timerStart - self._pausedTotal) : 0;
    if (liveAudioTrackCount(self) <= 1) {
        silenceDispatch(self, { slug: TOOL_SLUG, source: source, durMs: durMs });
    } else {
        // Multi-source: one track muting doesn't mean silence. Telemetry
        // only; the RMS monitor fires the warning if the MIX goes quiet.
        self._tel('record_audio_track_muted', { source: source, durMs: durMs, liveTracks: liveAudioTrackCount(self) });
    }
}

// ─────────────────────────────────────────────────────────────────────
// CODEC BLACKLIST (persistent, per-user/per-browser)
//
// Closes Ольга feedback #584 (2026-05-30) + the 5-event 7d cluster of
// `video-encoder-error: Encoding error.` (sids 09e408ca, 3a377e25 ×3,
// d784c76d — all RU, all WebCodecs, all High-profile AVC).
//
// Why this exists: `VideoEncoder.isConfigSupported(...)` returns
// `{supported: true}` for codecs that then ERROR at runtime — driver
// bugs, GPU/codec edge cases, or transient hardware issues. The cascade
// in init() trusts isConfigSupported and picks the first "supported"
// codec; if that one fails, every retry picks the same one and fails the
// same way. Olga's session 09e408ca emitted `video-encoder-error` at
// pipeline init (0 frames) — refresh would have given her the SAME
// codec choice and the SAME failure.
//
// Etalon answer: when an encoder errors at 0 frames, blacklist that
// codec for the user's browser for 24h. Next start() reads the blacklist
// and skips the failing codec, falls through to the next candidate
// (typically a lower H.264 profile or VP9). Self-healing without code
// changes per browser.
//
// 24h TTL because the underlying issue is usually transient (driver
// update, GPU pressure, etc.); we don't want to permanently lock a user
// out of H.264 if their setup recovers next day.
var CODEC_BLACKLIST_KEY = TOOL_SLUG + '.codec-blacklist.v1';
var CODEC_BLACKLIST_TTL_MS = 24 * 60 * 60 * 1000;

function getBlacklistedCodecs() {
    try {
        var raw = localStorage.getItem(CODEC_BLACKLIST_KEY);
        if (!raw) return [];
        var parsed = JSON.parse(raw);
        var now = Date.now();
        var fresh = parsed.filter(function (e) { return e && e.codec && e.ts && (now - e.ts) < CODEC_BLACKLIST_TTL_MS; });
        // Rewrite if any entries expired so the store stays bounded.
        if (fresh.length !== parsed.length) {
            try { localStorage.setItem(CODEC_BLACKLIST_KEY, JSON.stringify(fresh)); } catch (_) {}
        }
        return fresh.map(function (e) { return e.codec; });
    } catch (_) { return []; }
}

function pushCodecToBlacklist(codec) {
    if (!codec) return;
    try {
        var raw = localStorage.getItem(CODEC_BLACKLIST_KEY);
        var parsed = raw ? JSON.parse(raw) : [];
        // Drop existing same-codec entry; replace with fresh timestamp.
        parsed = parsed.filter(function (e) { return e && e.codec !== codec; });
        parsed.push({ codec: codec, ts: Date.now() });
        // Cap at 10 entries — beyond that we're flagging too aggressively.
        if (parsed.length > 10) parsed = parsed.slice(-10);
        localStorage.setItem(CODEC_BLACKLIST_KEY, JSON.stringify(parsed));
    } catch (_) {}
}

function newWebCodecsPipeline(self) {
    var muxer = null;
    var muxVideo = null;          // the door into muxer's video track (__muxVideoTrack, timbrica-core.js)
    var videoEncoder = null;
    var audioEncoder = null;
    var videoReader = null;
    var audioReader = null;
    var encoderRunning = false;
    var pauseUs = 0;          // accumulated paused micros (for timestamp fudge)
    var pauseStartUs = -1;
    var lastVideoTs = 0;
    var stats = { framesEncoded: 0, framesDropped: 0, audioFrames: 0, bytesOut: 0, queueDepthMax: 0, audioGaps: 0, audioGapMs: 0, audioGapsUnfilled: 0 };
    var startedAt = 0;
    var trackSettings = null;
    var audioCfg = null;
    // ── ONE FILE CLOCK FOR BOTH TRACKS (ported from screen-recorder, 2026-09-05) ──
    // Before this the muxer zeroed each track on its own first chunk: video on
    // the first frame the processor delivered after start(), audio on the first
    // mixer chunk. Those are different instants, and the difference is baked
    // into every file. Measured with a synthetic camera+mic that flash and beep
    // at the same instant (tests/playwright/webcam-recorder-av-offset.mjs):
    // audio 168–181 ms EARLY in 3/3 takes — lips move a sixth of a second after
    // the word. Now file time zero is T0 = performance.now() at start(); video
    // frames are bridged from their capture clock onto performance.now() via
    // the constant (timestamp − arrival) and stamped at the muxer; audio comes
    // from CaptureAudioTimeline, whose emitted stream IS the file clock (starts
    // at 0, silence for holes, pauses cut out). The mixer path below stays as
    // the fallback when the timeline module is not loaded.
    var timeline = {
        offsetUs: 0, pausedMs: 0, pauses: 0, _atMs: 0, lastV: -1,
        T0Ms: 0, videoOriginMs: 0, videoDomain: '', _vcal: 0,
        initVideoClock: function (rawUs, arrivalMs) {
            var d = rawUs / 1000 - arrivalMs;
            if (Math.abs(d) < 500) { this.videoDomain = 'perf'; this.videoOriginMs = 0; this._vcal = 99; }
            else { this.videoDomain = (rawUs / 1000 < 3600000) ? 'stream' : 'ticks'; this.videoOriginMs = d; this._vcal = 1; }
        },
        // Upward-only refinement over the first frames: a late read can only
        // make (ts − arrival) smaller, so the max is the truest constant.
        noteVideoFrame: function (rawUs, arrivalMs) {
            if (!this._vcal) { this.initVideoClock(rawUs, arrivalMs); return; }
            if (this._vcal >= 8) return;
            var d = rawUs / 1000 - arrivalMs;
            if (d > this.videoOriginMs) this.videoOriginMs = d;
            this._vcal++;
        },
        pause: function () { if (!this._atMs) { this._atMs = performance.now(); this.pauses++; } },
        resume: function () {
            if (!this._atMs) return;
            var ms = performance.now() - this._atMs;
            this._atMs = 0;
            this.pausedMs += ms;
            this.offsetUs += Math.round(ms * 1000);
        },
        // Monotonic by construction; a frame captured just before T0 lands on 0.
        stampVideo: function (us) {
            var t = Math.round(((us || 0) / 1000 - this.videoOriginMs - this.T0Ms) * 1000) - this.offsetUs;
            if (t <= this.lastV) t = this.lastV + 1;
            this.lastV = t; return t;
        }
    };
    var audioTl = null, audioTlStats = null, useTimeline = false;
    var pickedVideoCodec = '';
    var aborted = false;
    // OPFS state (default storage path):
    //   opfsRoot/opfsFileName is the temp file the muxer streams chunks to.
    //   opfsWritable is the open WritableStream we hand to mp4-muxer.
    //   opfsHandle is kept alive after stop() — fileHandle.getFile() backs the
    //   preview Blob, so we MUST NOT delete the OPFS file until the user
    //   moves on. Cleanup is the caller's job (next start() or pagehide).
    var opfsRoot = null;
    var opfsFileName = null;
    var opfsHandle = null;
    var opfsWritable = null;

    function adjustedTs(rawUs) {
        var base = rawUs - (pauseStartUs >= 0 ? (rawUs - pauseStartUs) + pauseUs : pauseUs);
        // Don't go negative — first frame may arrive with raw=0 then the
        // formula above is fine; later we treat startedAt as zero offset.
        return Math.max(0, base);
    }

    async function init(opts) {
        var displayStream = opts.displayStream;
        var micStream = opts.micStream;
        var videoTrack = displayStream.getVideoTracks()[0];
        if (!videoTrack) throw new Error('display-no-video-track');
        // Guard against the "track is already dead by the time we got here"
        // race — happens when the user closes the share-source tab between
        // permission grant and pipeline init. Without this guard we'd start
        // an encode loop on a dead stream that never produces frames; user
        // hits stop minutes later and gets nothing. (Suspected Pavel path.)
        if (videoTrack.readyState !== 'live') {
            throw new Error('display-track-already-ended');
        }

        trackSettings = videoTrack.getSettings();
        var width = (trackSettings.width || 1280);
        var height = (trackSettings.height || 720);
        // Some platforms report 0 — fall back. mp4-muxer requires non-zero
        // dimensions in the muxer config or it throws on first chunk.
        if (!width || !height) { width = 1280; height = 720; }
        // Even pixels — H.264 requires 4:2:0 sampling, so width/height
        // must be divisible by 2. Round up so we never crop content.
        width = Math.ceil(width / 2) * 2;
        height = Math.ceil(height / 2) * 2;

        var fps = opts.fps || 30;
        var quality = opts.quality || 'medium';
        var videoBitrate = bitrateFor(quality, width, height, fps);

        // Codec cascade. WebCodecs surface differs WILDLY by browser/OS:
        // 338 sessions/week were dropping to MediaRecorder fallback purely
        // because we only tried H.264 (no-h264-encoder telemetry). Now we
        // cascade through every codec mp4-muxer supports (avc/hevc/vp9/av1).
        // Most browsers that lack the proprietary H.264 encoder DO have VP9
        // since it's royalty-free; that single addition closes ~95% of the
        // fallback cluster. Each entry pairs the WebCodecs codec string with
        // the mp4-muxer container codec name + any encoder-specific config.
        var codecCandidates = [];
        // H.264 first — most efficient + best HW acceleration coverage.
        if (height > 1440 || (height > 1080 && fps > 30)) {
            codecCandidates.push({ webcodec: 'avc1.640033', muxer: 'avc', extra: { avc: { format: 'avc' } } });
        }
        if (height > 1080 || (height === 1080 && fps > 30)) {
            codecCandidates.push({ webcodec: 'avc1.64002A', muxer: 'avc', extra: { avc: { format: 'avc' } } });
        }
        codecCandidates.push({ webcodec: 'avc1.640028', muxer: 'avc', extra: { avc: { format: 'avc' } } });
        codecCandidates.push({ webcodec: 'avc1.42E01E', muxer: 'avc', extra: { avc: { format: 'avc' } } });
        // VP9 — royalty-free, widely available where WebCodecs is.
        // Profile 0 = 8-bit 4:2:0, universally decodable. Level matches 4K30 spec.
        codecCandidates.push({ webcodec: 'vp09.00.10.08', muxer: 'vp9', extra: {} });
        // AV1 — bleeding-edge but where HW available it's superior. Profile 0.
        codecCandidates.push({ webcodec: 'av01.0.04M.08', muxer: 'av1', extra: {} });

        // Read the persistent codec blacklist (24h TTL, written by our own
        // video-encoder.error handler on prior failed attempts). Skips codecs
        // that previously errored at runtime for this user even though
        // isConfigSupported() claimed they worked. Closes the "refresh and
        // hit the same Encoding error" loop documented in Ольга #584.
        var blacklistedCodecs = getBlacklistedCodecs();
        var encoderInfo = null;
        var pickedCandidate = null;
        for (var i = 0; i < codecCandidates.length; i++) {
            if (blacklistedCodecs.indexOf(codecCandidates[i].webcodec) !== -1) continue;
            var ck = await checkVideoEncoderConfig(codecCandidates[i].webcodec, width, height, videoBitrate, fps);
            if (ck && ck.supported) {
                pickedVideoCodec = codecCandidates[i].webcodec;
                pickedCandidate = codecCandidates[i];
                encoderInfo = ck;
                break;
            }
        }
        if (!encoderInfo) throw new Error('no-video-encoder');
        // Telemetry visibility: blacklist length + actual pick. Without
        // this we can't tell whether the blacklist is engaging on real
        // sessions or sitting dead.
        if (blacklistedCodecs.length) {
            self._tel('record_codec_blacklist_active', {
                blacklisted: blacklistedCodecs.join(','),
                picked: pickedVideoCodec
            });
        }

        // ── Container coherence (Bug 2026-06-14, YaBrowser sid 81ac5522
        //    "звука нет нигде в видео") ──────────────────────────────────
        // H.264 video → MP4 container + AAC audio. VP9/AV1 video → WebM
        // container + Opus audio. The old path forced EVERY codec into MP4,
        // so a browser without H.264 (YaBrowser, some Chromium) produced
        // VP9-video + AAC-audio in an MP4 — a poor-compatibility combo many
        // desktop players render with NO audio (and VP9-in-MP4 itself is
        // shaky in Win Movies&TV / QuickTime). WebM is the NATIVE, universally
        // playable container for VP9/AV1 + Opus, and is exactly what our
        // MediaRecorder fallback already ships. Opus also sidesteps Yandex's
        // flaky AAC encoder (the same session hit record_audio_dropped_mid
        // "Encoding error" mid-stream on AAC). H.264 stays MP4+AAC — the 95%
        // happy path, byte-for-byte unchanged.
        var useWebM = (pickedCandidate.muxer === 'vp9' || pickedCandidate.muxer === 'av1');

        // Audio cascade. MP4 path: AAC first (best player compat), then Opus.
        // WebM path: Opus ONLY — AAC is not a valid WebM/Matroska audio codec,
        // and Opus is the WebM-native pairing. (10 sessions/wk used to drop to
        // MR on no-aac-encoder; the WebM+Opus path now keeps those in WebCodecs.)
        // Audio front-end. Preferred: CaptureAudioTimeline (raw capture tracks
        // read with true capture timestamps, resampled onto the file clock,
        // 4 s stall buffer, holes filled where the timestamps say they are —
        // the module the screen recorder proved on 2026-09-02). Fallback when
        // the module is not on the page: the AudioContext mixer as before.
        useTimeline = !!(window.CaptureAudioTimeline && typeof window.CaptureAudioTimeline.create === 'function');
        var mixer = null, hasAudio = false, audioSources = [];
        if (useTimeline) {
            var dAud = displayStream ? displayStream.getAudioTracks() : [];
            if (dAud.length) audioSources.push({ kind: 'sys', track: dAud[0], gain: opts.sysVolume != null ? opts.sysVolume : 1.0 });
            if (micStream && micStream.getAudioTracks().length) audioSources.push({ kind: 'mic', track: micStream.getAudioTracks()[0], gain: opts.micVolume != null ? opts.micVolume : 1.0 });
            hasAudio = audioSources.length > 0;
        } else {
            mixer = buildMixer(displayStream, micStream, opts);
            hasAudio = !!mixer;
        }
        var pickedAudioCandidate = null;
        if (hasAudio) {
            // The timeline resamples every source to 48 kHz; the mixer follows
            // its context (usually 48 k, sometimes the device's 44.1 k).
            var sampleRate = useTimeline ? 48000 : (mixer.ctx.sampleRate || 48000);
            var audioCandidates = useWebM
                ? [ { webcodec: 'opus', muxer: 'A_OPUS' } ]
                : [ { webcodec: 'mp4a.40.2', muxer: 'aac' }, { webcodec: 'opus', muxer: 'opus' } ];
            for (var ai = 0; ai < audioCandidates.length; ai++) {
                var aCheck = await checkAudioEncoderConfig(audioCandidates[ai].webcodec, sampleRate, 2, audioBitrateFor(quality));
                if (aCheck && aCheck.supported) {
                    pickedAudioCandidate = audioCandidates[ai];
                    audioCfg = {
                        sampleRate: sampleRate, channels: 2,
                        codec: audioCandidates[ai].webcodec,
                        muxerCodec: audioCandidates[ai].muxer,
                        bitrate: audioBitrateFor(quality)
                    };
                    break;
                }
            }
            if (!audioCfg) {
                // No audio encoder available — fall through to MediaRecorder
                // rather than ship video-without-audio when user wanted audio.
                if (mixer) { try { mixer.ctx.close().catch(function(){}); } catch (_) {} }
                throw new Error('no-audio-encoder');
            }
            if (useTimeline) {
                audioTl = window.CaptureAudioTimeline.create({
                    sources: audioSources,
                    sampleRate: sampleRate,
                    channels: 2,
                    onEmit: onAudioEmit,
                    telemetry: function (name, payload) { self._tel(name, payload); }
                });
                mixer = audioTl;   // mimics the mixer surface: gains, analyser, ctx=null
            }
        }

        // Build muxer. Two write modes:
        //
        //   'memory' (default): ArrayBufferTarget + fastStart='in-memory'
        //     produces fully-finalized MP4 with moov at file start.
        //     Fast, simple, but holds the whole file in JS heap → hits the
        //     ~2 GB Chromium heap limit on long recordings (Andrey's
        //     "3 часа не сохраняются" bug, sid f01dbfcb 99-min 101k frames
        //     muxer-finalize:Array buffer allocation failed 2026-05-08).
        //
        //   'disk' (opt-in via UI toggle): FileSystemWritableFileStreamTarget
        //     streams chunks straight to the user's disk via the File System
        //     Access API. fastStart='fragmented' writes a streamable fMP4
        //     (every fragment plays standalone) so we never need to seek
        //     back and rewrite the moov atom — which is what blocks
        //     stream-to-disk on the regular fastStart path. The whole file
        //     never sits in memory, so multi-hour recordings work.
        //
        // The caller (orchestrator → blade) decides mode based on whether
        // a FileSystemFileHandle was passed via opts.fileHandle.
        // Pick the muxer library to match the container (see useWebM above).
        // Both expose the same surface: Muxer + ArrayBufferTarget +
        // FileSystemWritableFileStreamTarget + addVideoChunk/addAudioChunk +
        // finalize. Codec strings differ: mp4-muxer takes 'avc'/'vp9'/'aac'/
        // 'opus'; webm-muxer takes Matroska IDs ('V_VP9'/'V_AV1'/'A_OPUS').
        var MuxerNs = useWebM ? window.WebMMuxer : window.Mp4Muxer;
        if (!MuxerNs) throw new Error(useWebM ? 'webm-muxer-not-loaded' : 'mp4-muxer-not-loaded');
        var containerExt = useWebM ? 'webm' : 'mp4';
        var containerMime = useWebM ? 'video/webm' : 'video/mp4';
        self._containerExt = containerExt;
        self._containerMime = containerMime;

        // Default storage strategy: stream the encoded bytes straight to an
        // OPFS-backed file so the JS heap never holds the full recording.
        //
        // Why not ArrayBufferTarget anymore: it accumulates the entire MP4 in
        // a single Uint8Array on the JS heap. Chromium caps that allocation
        // at ~2 GB and muxer.finalize() crashes with "Array buffer allocation
        // failed" on multi-hour recordings — which is what hit feedback #366
        // (Andrey, 99-min recording lost). 3 telemetry events of this exact
        // class in 24h before this rewrite (sids f01dbfcb + 4c132bf5×2).
        //
        // Why OPFS over showSaveFilePicker (the previous opt-in fix): OPFS is
        // Origin Private File System — a sandboxed disk-backed filesystem
        // available without ANY user picker or permission prompt. The
        // earlier toggle helped only the ~5% of users who knew to enable it
        // and clicked through the picker; OPFS solves the bug for 100% of
        // users by default. Chrome 102+ / Firefox 111+ / Safari 15.2+ —
        // covers ≥95% of real visitors. The rare uncovered tail uses the
        // ArrayBuffer fallback below (and accepts the OOM risk).
        //
        // Why fastStart='in-memory' is OK with OPFS (vs the 'fragmented'
        // we used with the picker path): FileSystemWritableFileStream
        // SUPPORTS .seek(), so muxer.finalize() can seek back to offset 0
        // and write the moov atom at the file head — universal MP4 layout,
        // works in every desktop player including Win11 Movies & TV.
        var muxerTarget;
        var storageMode = 'memory';
        if (typeof navigator.storage !== 'undefined' &&
            typeof navigator.storage.getDirectory === 'function') {
            try {
                opfsRoot = await navigator.storage.getDirectory();
                opfsFileName = '_' + TOOL_SLUG + '_' + Date.now() + '_' +
                    Math.random().toString(36).slice(2, 8) + '.' + containerExt;
                opfsHandle = await opfsRoot.getFileHandle(opfsFileName, { create: true });
                opfsWritable = await opfsHandle.createWritable();
                // Exact-size writes through the shared target: WebKit writes the
                // whole chunk buffer behind a subarray view (Safari 26 pads the
                // file to 16 MiB multiples; a seek-back patch would overwrite
                // it). See __opfsMuxTarget in timbrica-core.js.
                muxerTarget = window.__opfsMuxTarget(MuxerNs, opfsWritable);
                storageMode = 'opfs';
                self._opfsRoot = opfsRoot;
                self._opfsFileName = opfsFileName;
            } catch (e) {
                // OPFS open failed (private mode quirks, very rare). Fall
                // through to ArrayBuffer — accepts OOM risk on long files
                // but at least lets users record at all.
                try { console.warn('[' + TOOL_SLUG + '] OPFS unavailable, falling back to memory:', e && e.message); } catch (_) {}
                opfsRoot = null; opfsFileName = null; opfsHandle = null; opfsWritable = null;
            }
        }
        if (!muxerTarget) {
            muxerTarget = new MuxerNs.ArrayBufferTarget();
        }

        // Video codec ID per container: mp4-muxer uses 'avc'/'vp9'/'av1';
        // webm-muxer uses Matroska IDs.
        var muxerVideoCodec = useWebM
            ? (pickedCandidate.muxer === 'av1' ? 'V_AV1' : 'V_VP9')
            : pickedCandidate.muxer;
        var muxerCfg = {
            target: muxerTarget,
            // mp4-muxer's `frameRate` IS the track timescale — fixed, not the capture
            // rate (a webcam's real rate wanders with light). Rationale + measurements:
            // MUX_VIDEO_TIMEBASE in webcodecs-pipeline.js. Held by Mp4MuxerTimebaseTest.
            video: { codec: muxerVideoCodec, width: width, height: height, frameRate: 120000 },
            audio: hasAudio ? {
                codec: audioCfg.muxerCodec,
                numberOfChannels: audioCfg.channels,
                sampleRate: audioCfg.sampleRate
            } : undefined,
            firstTimestampBehavior: 'offset'
        };
        // fastStart is an MP4-only concept (WebM streams forward natively).
        // CRITICAL (Bug 2026-06-16..19: muxer-finalize "Array buffer
        // allocation failed" on 50min–6.3HOUR recordings, 7 events/7d):
        // mp4-muxer's fastStart:'in-memory' holds EVERY encoded sample's
        // bytes in JS RAM until finalize (verified mp4-muxer.js:1825-1827 —
        // 'in-memory' skips the progressive write; 1829-1834 — the streaming
        // path writes+nulls each sample as chunks fill). So 'in-memory'
        // DEFEATS the OPFS target: nothing reaches disk until finalize, and a
        // multi-hour recording OOMs. The "OPFS = OOM-safe" claim was only ever
        // true in spirit — the fastStart mode silently re-buffered everything.
        //   • OPFS (streaming target) → fastStart:false. mdat streams to disk
        //     as chunks arrive (sample.data nulled immediately), moov written
        //     at the END. Flat memory for ANY duration. moov-at-end MP4 is
        //     standard and plays in every LOCAL player (Win Movies&TV / VLC /
        //     QuickTime / browsers) — faststart-at-head only matters for HTTP
        //     progressive streaming, which a downloaded blob is not.
        //   • Memory fallback (ArrayBufferTarget, no OPFS) → 'in-memory' is
        //     fine: the whole file is in RAM regardless, moov-at-start is a
        //     free bonus, and the onWarning('memory_storage…') already caps
        //     expectations for that rare no-OPFS cohort.
        if (!useWebM) muxerCfg.fastStart = (storageMode === 'opfs') ? false : 'in-memory';
        muxer = new MuxerNs.Muxer(muxerCfg);
        // Parameter sets that change mid-recording travel in-band (__muxVideoTrack).
        muxVideo = window.__muxVideoTrack ? window.__muxVideoTrack(muxer) : function (c, m, t, o) { return muxer.addVideoChunk(c, m, t, o); };
        self._storageMode = storageMode;

        // Video encoder. mp4-muxer needs a defaulted `colorSpace` on the
        // first chunk's decoderConfig — same defensive patch as webcodecs-
        // pipeline.js (sid c913580e bug).
        var DEFAULT_COLOR_SPACE = { primaries: 'bt709', transfer: 'bt709', matrix: 'bt709', fullRange: false };
        var firstChunk = true;
        videoEncoder = new VideoEncoder({
            output: function (chunk, meta) {
                if (aborted) return;
                var safe = meta || {};
                if (firstChunk) {
                    firstChunk = false;
                    if (!safe.decoderConfig) {
                        safe = { decoderConfig: { codec: pickedVideoCodec, codedWidth: width, codedHeight: height, colorSpace: DEFAULT_COLOR_SPACE } };
                    } else if (!safe.decoderConfig.colorSpace) {
                        safe = { decoderConfig: Object.assign({}, safe.decoderConfig, { colorSpace: DEFAULT_COLOR_SPACE }) };
                    }
                }
                try {
                    // Timeline mode: frames were encoded with their RAW capture
                    // timestamps; the file time is decided here, on one clock
                    // with the audio. Fallback mode keeps the muxer's own zero.
                    if (useTimeline) muxVideo(chunk, safe, timeline.stampVideo(chunk.timestamp));
                    else muxVideo(chunk, safe);
                    stats.framesEncoded++;
                    stats.bytesOut += chunk.byteLength;
                    var qd = videoEncoder ? videoEncoder.encodeQueueSize : 0;
                    if (qd > stats.queueDepthMax) stats.queueDepthMax = qd;
                } catch (e) {
                    // Quota-exceeded path: don't blow up the recording. Mark
                    // the flag so stop() knows to try finalize anyway (with
                    // a small chance the moov atom fits) and reports the
                    // truth to the user via the audioMissing-style banner.
                    // 10 events/wk hit this without graceful handling
                    // pre-fix; users lost everything to a quota glitch.
                    var em = (e && e.message || '').toLowerCase();
                    if (/exceed.*quota|quotaexceedederror/i.test(em)) {
                        if (!self._quotaExceededMid) {
                            self._quotaExceededMid = true;
                            self._tel('record_quota_exceeded_mid', {
                                msg: (e && e.message || '').slice(0, 120),
                                framesEncoded: stats.framesEncoded,
                                bytesOut: stats.bytesOut
                            });
                            encoderRunning = false; // stop the loop politely
                        }
                        // Drop further chunks silently — disk is full anyway.
                        return;
                    }
                    self._fail(new Error('mux-video-failed: ' + e.message));
                }
            },
            error: function (e) {
                // VideoEncoder runtime error. Pre-fix this called _fail() and
                // killed the entire recording even when we had 5 minutes of
                // perfectly good frames on OPFS (3a377e25 ×3 cluster). Two
                // distinct failure modes are handled here, plus the
                // pre-existing 0-frame fast-fail.
                if (self._videoEncoderErroredMid) return; // idempotent
                self._videoEncoderErroredMid = true;
                var emsg = (e && e.message || 'unknown').slice(0, 160);
                // "Codec reclaimed due to inactivity" is the OS / Chromium
                // reclaiming the hardware encoder while the tab is hidden
                // or idle — NOT a codec defect. 4 events/7d on prod (sids
                // 2d8426c5, 3a377e25, 018c69a8, 730439e0 — all had real
                // frames on disk). Don't blacklist the codec for this
                // class; the codec works fine when the tab is in focus.
                var isCodecReclaim = /reclaim|inactivity/i.test(emsg);
                self._videoEncoderReclaimed = isCodecReclaim;
                self._tel('record_video_encoder_error_mid', {
                    msg: emsg,
                    framesEncoded: stats.framesEncoded,
                    durMs: lastVideoTs ? Math.round(lastVideoTs / 1000) : 0,
                    codec: pickedVideoCodec,
                    storageMode: storageMode,
                    isCodecReclaim: isCodecReclaim
                });
                // Blacklist ONLY on true codec failures, not OS reclaim.
                // Pre-fix the cascade would skip H.264 forever after one
                // reclaim event — wrong, that's a Wake Lock issue not a
                // codec issue. And a codec that already encoded part of this
                // take works: an error mid-take comes from its environment
                // (the GPU process lost — measured on the screen recorder,
                // 26.09.2026), so only a codec that never produced a frame is
                // written off (prod, 30 d: 1 «Encoding error.» with frames
                // cost that user H.264 for a day).
                if (!isCodecReclaim && stats.framesEncoded === 0) {
                    pushCodecToBlacklist(pickedVideoCodec);
                }
                // 0 frames means we have nothing to preserve. Fail loud with
                // a typed error so the blade can show a useful CTA. Reclaim
                // and codec-failure get distinct error codes so the blade
                // can surface different messages (reclaim → keep tab in
                // foreground; codec → codec disabled for 24h).
                if (stats.framesEncoded === 0) {
                    var feCode = isCodecReclaim ? 'video-encoder-reclaimed-at-start' : 'video-encoder-failed-at-start';
                    var fe = new Error(feCode + ': ' + emsg);
                    fe.code = feCode;
                    fe.userFacing = true;
                    self._fail(fe);
                    return;
                }
                // Otherwise: graceful preserve. Stop the loop politely; stop()
                // will flush the muxer with whatever chunks made it to disk
                // and surface result.videoEncoderErroredMid + reclaimed so
                // the blade banner explains the partial file. Same shape as
                // quota-exceeded-mid above and audio-encoder.error below.
                encoderRunning = false;
            }
        });
        // Build encoder config. Codec-specific extras (e.g. avc.format='avc'
        // which selects AVCC stream format for H.264) come from the
        // candidate descriptor so VP9/AV1 don't get the AVC key by mistake.
        var encConfig = {
            codec: pickedVideoCodec,
            width: width, height: height,
            bitrate: videoBitrate,
            framerate: fps,
            bitrateMode: 'variable',
            latencyMode: 'realtime',
            hardwareAcceleration: encoderInfo.hardware ? 'prefer-hardware' : 'no-preference'
        };
        if (pickedCandidate.extra) Object.assign(encConfig, pickedCandidate.extra);
        videoEncoder.configure(encConfig);

        if (hasAudio) {
            if (!window.AacAccessUnit) throw new Error('aac-access-unit-not-loaded');
            // Through the AAC door: raw access units + a guaranteed
            // decoderConfig.description on the first chunk (Opus passes
            // untouched) — see aac-access-unit.js, report #6990.
            audioEncoder = window.AacAccessUnit.encoder({
                output: function (chunk, meta) {
                    if (aborted) return;
                    try {
                        muxer.addAudioChunk(chunk, meta || {});
                        stats.audioFrames++;
                    } catch (e) {
                        var em2 = (e && e.message || '').toLowerCase();
                        if (/exceed.*quota|quotaexceedederror/i.test(em2)) {
                            if (!self._quotaExceededMid) {
                                self._quotaExceededMid = true;
                                self._tel('record_quota_exceeded_mid', {
                                    msg: (e && e.message || '').slice(0, 120),
                                    source: 'audio'
                                });
                                encoderRunning = false;
                            }
                            return;
                        }
                        self._fail(new Error('mux-audio-failed: ' + e.message));
                    }
                },
                error: function (e) {
                    // Audio-encoder mid-recording errors used to call _fail
                    // and kill the whole recording. That's a hammer: 6/week
                    // sessions lost their entire video over an audio glitch.
                    // Graceful degradation: log + detach audio, let video
                    // continue, mark the result as audio-dropped so the
                    // blade can warn the user.
                    self._tel('record_audio_dropped_mid', {
                        msg: (e && e.message || '').slice(0, 120),
                        afterFrames: stats.audioFrames,
                        afterMs: lastVideoTs ? Math.round(lastVideoTs / 1000) : 0
                    });
                    self._audioDroppedMid = true;
                    try { audioEncoder.close(); } catch (_) {}
                    audioEncoder = null;
                    // Stop the audio read loop too — its next .read() would
                    // try to encode() on a closed encoder and throw.
                    try { if (audioReader) audioReader.cancel().catch(function(){}); } catch (_) {}
                    audioReader = null;
                }
            });
            audioEncoder.configure({
                codec: audioCfg.codec,
                sampleRate: audioCfg.sampleRate,
                numberOfChannels: audioCfg.channels,
                bitrate: audioCfg.bitrate
            });
        }

        // Frame producers. We keep references to the readers so cancel() can
        // tear them down deterministically.
        // maxBufferSize: the default queues are tiny (ONE video frame, ~100 ms
        // of audio) and whatever the encode loops cannot read during a
        // main-thread stall is dropped for good. Measured 2026-09-05
        // (tests/scratch/audio-mstp-stall-scratch.mjs): a 300 ms stall lost
        // 193 ms of audio, 700 ms lost 595 ms. A lost VIDEO frame is a stutter
        // (timestamps stay true); lost AUDIO is a desync for the rest of the
        // take, because the AAC/Opus encoder stamps its output by SAMPLE COUNT
        // and the hole simply disappears (the seamless-pause stand's
        // «video 3.11 s vs audio 2.50 s»). Four seconds of 10 ms chunks is
        // ~1.6 MB — covers any stall short of the tab being killed; the gap
        // fill in audioLoop() covers the rest.
        var videoProcessor = new MediaStreamTrackProcessor({ track: videoTrack, maxBufferSize: 6 });
        videoReader = videoProcessor.readable.getReader();
        if (hasAudio && !audioTl) {
            var aTrack = mixer.dest.stream.getAudioTracks()[0];
            var audioProcessor = new MediaStreamTrackProcessor({ track: aTrack, maxBufferSize: 400 });
            audioReader = audioProcessor.readable.getReader();
        }

        self._mixer = mixer;
        self._displayStream = displayStream;
        self._micStream = micStream;

        return {
            width: width, height: height, fps: fps,
            videoCodec: pickedVideoCodec,
            audioCodec: hasAudio ? 'mp4a.40.2' : null,
            hardware: encoderInfo.hardware,
            videoBitrate: videoBitrate,
            audioBitrate: hasAudio ? audioCfg.bitrate : 0
        };
    }

    // Rebuild an AudioData with a shifted timestamp (for seamless pause). The
    // PCM is copied out in its native format and re-wrapped. Returns null if
    // the format is unsupported — the caller then leaves seamless pause off so
    // audio never drifts out of sync with the (also-unshifted) video.
    function reTimestampAudio(data, newTs) {
        try {
            var fmt = data.format;
            var nFr = data.numberOfFrames, nCh = data.numberOfChannels, sr = data.sampleRate;
            if (!fmt || nFr <= 0 || nCh <= 0) return null;
            var planar = fmt.indexOf('-planar') !== -1;
            var Ctor = fmt.indexOf('f32') === 0 ? Float32Array
                     : fmt.indexOf('s16') === 0 ? Int16Array
                     : fmt.indexOf('s32') === 0 ? Int32Array
                     : fmt.indexOf('u8') === 0 ? Uint8Array : null;
            if (!Ctor) return null;
            var buf = new Ctor(nFr * nCh);
            if (planar) {
                for (var c = 0; c < nCh; c++) { data.copyTo(buf.subarray(c * nFr, (c + 1) * nFr), { planeIndex: c }); }
            } else {
                data.copyTo(buf, { planeIndex: 0 });
            }
            return new AudioData({ format: fmt, sampleRate: sr, numberOfFrames: nFr, numberOfChannels: nCh, timestamp: newTs, data: buf });
        } catch (e) { return null; }
    }

    async function videoLoop() {
        var keyFrameCounter = 0;
        var keyFrameInterval = 60; // every ~2 s @30 fps
        // Monotonic-timestamp guard. mp4-muxer rejects out-of-order frames
        // with "Timestamps must be monotonically increasing" — 8 sessions/wk
        // hit this when pause/resume reset the source's frame-clock or when
        // the OS hiccuped and re-delivered a buffered frame. Drop any frame
        // whose timestamp is not strictly newer than the last we encoded.
        var lastEncodedTs = -1;
        while (encoderRunning && !aborted) {
            var r;
            try { r = await videoReader.read(); }
            catch (e) { break; }
            if (r.done) break;
            var frame = r.value;
            if (self._state === 'paused') {
                frame.close();
                continue;
            }
            var raw = frame.timestamp || 0;
            // Seamless pause: subtract the accumulated paused span so the output
            // timeline has no freeze-gap. Disabled (off=0) when the recording has
            // audio that can't be re-timestamped (self._audioRebaseOk===false),
            // so video never drifts away from audio. lastEncodedTs tracks OUTPUT
            // ts → monotonic guard stays correct across the rebase.
            // Timeline mode: no rebase here — the frame goes to the encoder with
            // its raw capture timestamp and the muxer stamp (stampVideo) removes
            // the paused span, on the same clock the audio timeline uses.
            if (useTimeline) timeline.noteVideoFrame(raw, performance.now());
            var off = useTimeline ? 0 : ((self._seamlessOk === false || self._audioRebaseOk === false) ? 0 : (self._pausedTotalUs || 0));
            var outTs = raw - off;
            if (outTs <= lastEncodedTs) {
                // Backward-or-equal OUTPUT timestamp — drop to keep monotonic order.
                stats.framesDropped++;
                frame.close();
                continue;
            }
            var keyFrame = (keyFrameCounter % keyFrameInterval) === 0;
            try {
                if (off > 0) {
                    var vf = new VideoFrame(frame, { timestamp: outTs });
                    videoEncoder.encode(vf, { keyFrame: keyFrame });
                    vf.close();
                } else {
                    videoEncoder.encode(frame, { keyFrame: keyFrame });
                }
                keyFrameCounter++;
                lastVideoTs = raw;
                lastEncodedTs = outTs;
            } catch (e) {
                stats.framesDropped++;
            } finally {
                frame.close();
            }
        }
    }

    // Timeline mode: emitted blocks already sit on the file clock.
    function onAudioEmit(planes, frames, startUs) {
        if (!audioEncoder || !audioCfg || aborted) return;
        var ch = planes.length;
        var data = new Float32Array(frames * ch);
        for (var c = 0; c < ch; c++) data.set(planes[c], c * frames);
        var ad = null;
        try {
            ad = new AudioData({ format: 'f32-planar', sampleRate: audioCfg.sampleRate, numberOfFrames: frames, numberOfChannels: ch, timestamp: startUs, data: data });
            audioEncoder.encode(ad);
        } catch (_) {}
        finally { if (ad) { try { ad.close(); } catch (_) {} } }
    }

    // Gap-fill state (see audioLoop). Expected start of the next chunk in
    // SOURCE time; reset on every resume so the paused span is not "filled".
    var audioExpectTs = null, audioResumeSeq = -1;
    var AUDIO_GAP_MIN_US = 15000;        // > one 10 ms chunk + jitter
    var AUDIO_GAP_MAX_US = 30 * 1000000; // beyond this the source is gone, not stalled
    // Encode `gapUs` of silence shaped like `like` (format / rate / channels),
    // in chunks of ≤1024 frames so the encoder sees ordinary-sized input.
    function encodeSilence(like, startTs, gapUs) {
        if (!audioEncoder) return;
        var sr = like.sampleRate, nCh = like.numberOfChannels, fmt = like.format || 'f32-planar';
        var total = Math.round(gapUs / 1e6 * sr), ts = startTs;
        var Ctor = fmt.indexOf('f32') === 0 ? Float32Array : fmt.indexOf('s16') === 0 ? Int16Array : fmt.indexOf('s32') === 0 ? Int32Array : fmt.indexOf('u8') === 0 ? Uint8Array : Float32Array;
        var zero = (fmt.indexOf('u8') === 0) ? 128 : 0;
        while (total > 0) {
            var n = Math.min(1024, total);
            var buf = new Ctor(n * nCh); if (zero) buf.fill(zero);
            var silent = new AudioData({ format: fmt, sampleRate: sr, numberOfFrames: n, numberOfChannels: nCh, timestamp: ts, data: buf });
            try { audioEncoder.encode(silent); } finally { silent.close(); }
            ts += Math.round(n / sr * 1e6); total -= n;
        }
    }

    async function audioLoop() {
        if (!audioReader) return;
        while (encoderRunning && !aborted) {
            // Encoder may have been detached mid-recording by the
            // graceful-audio-degradation path (audioEncoder.error handler
            // sets it to null + cancels the reader). Bail clean.
            if (!audioEncoder || !audioReader) break;
            var r;
            try { r = await audioReader.read(); }
            catch (e) { break; }
            if (r.done) break;
            var data = r.value;
            if (self._state === 'paused') { data.close(); continue; }
            // Probe AudioData re-timestamp support ONCE (before any pause can
            // happen). If unsupported, seamless pause stays off for the whole
            // recording (both loops read self._audioRebaseOk) so audio/video
            // never drift apart.
            if (self._audioRebaseOk === undefined) {
                var probe = reTimestampAudio(data, data.timestamp || 0);
                self._audioRebaseOk = !!probe;
                if (probe) { try { probe.close(); } catch (_) {} }
            }
            var aoff = (self._seamlessOk === false || self._audioRebaseOk === false) ? 0 : (self._pausedTotalUs || 0);
            // ── Gap fill ──
            // The encoder stamps output by sample count, so a hole in the input
            // (chunks the processor dropped during a stall) would silently
            // shorten the audio and pull it ahead of the video for good. Keep
            // sample-continuity: when the source timestamp jumps past where the
            // previous chunk ended, encode silence for the missing span first.
            // The first chunk after a resume is exempt — that jump is the pause
            // itself, handled by the rebase above.
            var rawTs = data.timestamp || 0;
            var durUs = Math.round(data.numberOfFrames / data.sampleRate * 1e6);
            if (audioResumeSeq !== self._resumeSeq) { audioExpectTs = null; audioResumeSeq = self._resumeSeq; }
            if (audioExpectTs !== null && rawTs - audioExpectTs > AUDIO_GAP_MIN_US) {
                var gapUs = rawTs - audioExpectTs;
                if (gapUs <= AUDIO_GAP_MAX_US) {
                    try { encodeSilence(data, audioExpectTs - aoff, gapUs); } catch (_) {}
                    stats.audioGapMs += Math.round(gapUs / 1000);
                    stats.audioGaps++;
                    if (stats.audioGaps === 1) self._tel('record_audio_gap', { ms: Math.round(gapUs / 1000) });
                } else {
                    // Longer than we are willing to fabricate — the source went
                    // away (device lost / context suspended). Reported, not filled.
                    stats.audioGapsUnfilled++;
                    if (stats.audioGapsUnfilled === 1) self._tel('record_audio_gap_unfilled', { ms: Math.round(gapUs / 1000) });
                }
            }
            audioExpectTs = rawTs + durUs;
            try {
                if (audioEncoder) {
                    if (aoff > 0) {
                        var ad = reTimestampAudio(data, rawTs - aoff);
                        if (ad) { audioEncoder.encode(ad); ad.close(); }
                        else { audioEncoder.encode(data); }
                    } else {
                        audioEncoder.encode(data);
                    }
                }
            } catch (_) {}
            data.close();
        }
    }

    function start() {
        encoderRunning = true;
        startedAt = performance.now();
        timeline.T0Ms = startedAt;   // file zero for BOTH tracks
        videoLoop();
        if (audioTl) audioTl.start(startedAt);
        else audioLoop();
        // Silence + AudioContext monitors via the shared RecordingUtils
        // primitive (so the MediaRecorder pipeline gets identical coverage —
        // see attachSilenceMonitors). Detects audio that goes silent on an
        // alive track (Дмитрий #738), survives tab-backgrounding (setTimeout
        // not rAF), peak+RMS dual gate to avoid pianissimo/pause false-fires.
        attachSilenceMonitors(self, function () { return encoderRunning && !aborted; });
    }

    function pause() {
        timeline.pause();
        if (audioTl) audioTl.pause(performance.now());
        if (pauseStartUs < 0) pauseStartUs = (lastVideoTs || 0);
    }
    function resume() {
        timeline.resume();
        if (audioTl) audioTl.resume(performance.now());
        if (pauseStartUs >= 0) {
            // Note: timestamp-fudging is best-effort. WebCodecs gives us
            // monotonic frame timestamps from the source; we're not actually
            // changing them in this V1, so paused intervals show as a brief
            // freeze in the output. Phase 2 will re-base via VideoFrame.copy.
            pauseStartUs = -1;
        }
    }

    async function stop() {
        encoderRunning = false;
        // Container ext/mime were decided in init() (sibling fn); read via the
        // self-anchored refs (default mp4 for safety if init bailed early).
        var cExt = self._containerExt || 'mp4';
        var cMime = self._containerMime || 'video/mp4';
        // Tear down silence/ctx monitors first — encoderRunning=false already
        // makes their isRunning() predicate false, but explicit stop() frees
        // the listener + ends the poll loop deterministically.
        if (self._silenceMon) { try { self._silenceMon.stop(); } catch (_) {} }
        if (self._ctxMon) { try { self._ctxMon.stop(); } catch (_) {} }
        // Every wait below is BOUNDED (recorder-stop.js, shared with the screen
        // recorder): a stop that waits on a codec the GPU process took with it
        // never ends, and the user is left with «Saving…» and no file. What a
        // timed-out step still held is lost; what already reached the muxer is
        // written by finalize() below.
        var phases = self._stopPhases = [];
        async function step(name, p, ms) {
            var t = performance.now();
            self._stopPhase = name;
            var res = await bounded(p, ms);
            phases.push({ p: name, ms: Math.round(performance.now() - t), to: !!res.timedOut });
            if (res.timedOut) self._tel('record_stop_step_timeout', { step: name, ms: ms, framesEncoded: stats.framesEncoded, audioFrames: stats.audioFrames });
            return res;
        }
        if (videoReader) await step('reader', videoReader.cancel(), 3000);
        if (audioReader) await step('audio-reader', audioReader.cancel(), 3000);
        // Drain the audio timeline BEFORE flushing the encoder: its tail
        // (everything captured up to the moment Stop was pressed, including what
        // still waited in the processor's queue — the tracks are still live) is
        // emitted here.
        if (audioTl) {
            var tlr = await step('audio-timeline', audioTl.stop(self._stopAtMs, { wait: bounded }), 8000);
            if (tlr.ok) audioTlStats = tlr.value;
            else { try { audioTl.abort(); } catch (_) {} }
        }
        // Skip videoEncoder.flush() when the encoder is already in error
        // state — calling flush() on a dead encoder throws InvalidStateError,
        // which used to mask the underlying error message at finalize time.
        if (!self._videoEncoderErroredMid) await step('video-flush', videoEncoder.flush(), 10000);
        if (audioEncoder) await step('audio-flush', audioEncoder.flush(), 8000);
        try { videoEncoder.close(); } catch (_) {}
        try { if (audioEncoder) audioEncoder.close(); } catch (_) {}

        // LOUD FAIL: 0 frames encoded means the encode loop never saw a
        // frame from MediaStreamTrackProcessor. Most common cause: the
        // share-source tab/window died before our first read(). User
        // expects a recording — silently producing an empty file or
        // crashing the muxer is the worst possible outcome (hit Pavel
        // 2026-05-08). Throw a typed error so the orchestrator surfaces
        // a clear message instead of a generic toast.
        if (stats.framesEncoded === 0) {
            var err = new Error('recording-empty-no-frames');
            err.userFacing = true;
            throw err;
        }

        try { muxer.finalize(); } catch (e) {
            // Quota-exceeded at finalize is the "expected" tail of the
            // mid-recording quota path. We already stopped the encode
            // loop on the first quota event, but moov atom + final box
            // writes need additional bytes; if they don't fit either,
            // the partial file on disk is unfinalized. Throw a typed
            // error so the blade shows the disk-full banner.
            var em3 = (e && e.message || '').toLowerCase();
            if (/exceed.*quota|quotaexceedederror/i.test(em3) || self._quotaExceededMid) {
                self._tel('record_quota_finalize_fail', {
                    msg: (e && e.message || '').slice(0, 120),
                    framesEncoded: stats.framesEncoded
                });
                var qerr = new Error('quota-exceeded: ' + (e && e.message || 'disk full'));
                qerr.userFacing = true;
                qerr.code = 'quota-exceeded';
                throw qerr;
            }
            var ferr = new Error('muxer-finalize: ' + e.message);
            ferr.userFacing = true;
            throw ferr;
        }

        // Audio expectation vs reality. We compare USER INTENT (opts.captureSysAudio
        // or opts.captureMic, captured at init time) against what actually
        // got encoded. If the user asked for ANY audio and the file has 0
        // audio frames, surface it as `audioMissing` so the blade can show
        // a sticky warning. Two failure modes this catches:
        //   (a) wanted sys-audio, picked Whole-screen → buildMixer returns
        //       null → audioCfg not set → silent file (Кот #534 case)
        //   (b) wanted both, mic permission denied + no sys-audio → same
        //   (c) mixer built but encoder emitted 0 chunks before stop
        // Whichever it is, the user's expectation was "audio in file" and
        // reality is "silent file". They deserve to know.
        var userWantedAudio = !!self._userWantedAudio;
        var actualAudioFrames = stats.audioFrames || 0;
        var audioMissing = userWantedAudio && actualAudioFrames === 0;
        var audioDroppedMid = !!self._audioDroppedMid;
        var quotaExceededMid = !!self._quotaExceededMid;
        var videoEncoderErroredMid = !!self._videoEncoderErroredMid;
        var videoEncoderReclaimed = !!self._videoEncoderReclaimed;
        var audioSilenceMid = !!self._audioSilenceMid;
        var audioContextSuspended = !!self._audioContextSuspended;

        // OPFS path: close the writable so the file is fully flushed, then
        // read it back as a Blob via fileHandle.getFile(). The Blob is a
        // VIEW onto the OPFS file (no copy), so the file MUST stay alive
        // for as long as the caller uses the Blob (preview, download).
        // Cleanup is the orchestrator's responsibility — it deletes the
        // OPFS file on next start() or pagehide.
        if (opfsHandle) {
            // closing commits the file: a big take needs its time, a dead
            // writer must not hold the take forever
            var cl = await step('opfs-close', opfsWritable.close(), 60000 + 30000 * (stats.bytesOut || 0) / 1073741824);
            if (!cl.ok) {
                var e = cl.error || (function () { var te = new Error('the file was not committed in time'); te.name = 'TimeoutError'; return te; })();
                // CLEANUP: the partial OPFS file is still on disk and counts
                // toward quota. Without this delete, EACH retry on a near-full
                // quota leaves another partial file (session 8f56cfd3 today:
                // 3 retries × ~10-30 MB orphans = quota gets progressively
                // worse, every subsequent attempt also fails). The sweep at
                // next start() respects the 5-min safety window so won't catch
                // a fresh failure either.
                try { await opfsRoot.removeEntry(opfsFileName); } catch (_) {}
                opfsWritable = null;
                opfsHandle = null;
                opfsFileName = null;
                var em4 = (e && e.message || '').toLowerCase();
                var werr = new Error('opfs-writable-close: ' + (e && e.message || String(e)));
                werr.userFacing = true;
                // Quota-family signature: treat the same as quota-exceeded
                // at finalize so the blade routes to the disk-full banner
                // instead of a generic "couldn't save".
                if (/exceed.*quota|quotaexceedederror|cause.*data loss/i.test(em4)) {
                    werr.code = 'quota-exceeded';
                }
                throw werr;
            }
            opfsWritable = null;
            var file = await opfsHandle.getFile();
            // Register in the user's persistent recordings library so it
            // survives across page reloads and shows up in the history UI.
            // Only the OPFS path goes here — the MR-memory fallback doesn't
            // have a persistable file (chunks live in JS heap only).
            if (window.RecordingsHistory && typeof window.RecordingsHistory.save === 'function') {
                try {
                    await step('history-save', window.RecordingsHistory.save({
                        fileName: opfsFileName,
                        createdAt: parseInt(opfsFileName.match(new RegExp('_' + TOOL_SLUG + '_(\\d+)_'))[1], 10) || Date.now(),
                        durMs: 0, // filled in by orchestrator (knows actual durMs from timer)
                        sizeBytes: file.size,
                        mime: cMime,
                        ext: cExt,
                        audioMissing: audioMissing,
                        audioDroppedMid: audioDroppedMid,
                        quotaExceededMid: quotaExceededMid,
                        videoEncoderErroredMid: videoEncoderErroredMid,
                        audioSilenceMid: audioSilenceMid,
                        label: null
                    }), 5000);
                } catch (_) {}
            }
            return {
                blob: new Blob([file], { type: cMime }),
                mime: cMime,
                ext: cExt,
                storageMode: 'opfs',
                opfsFileName: opfsFileName,
                stats: stats,
                audioMissing: audioMissing,
                audioDroppedMid: audioDroppedMid,
                audioExpected: userWantedAudio,
                quotaExceededMid: quotaExceededMid,
                videoEncoderErroredMid: videoEncoderErroredMid,
                videoEncoderReclaimed: videoEncoderReclaimed,
                audioSilenceMid: audioSilenceMid,
                audioContextSuspended: audioContextSuspended
            };
        }

        // Memory fallback: regular ArrayBuffer-backed Blob.
        var buf = muxer.target.buffer;
        return {
            blob: new Blob([buf], { type: cMime }),
            mime: cMime,
            ext: cExt,
            storageMode: 'memory',
            stats: stats,
            audioMissing: audioMissing,
            audioDroppedMid: audioDroppedMid,
            audioExpected: userWantedAudio,
            quotaExceededMid: quotaExceededMid,
            videoEncoderErroredMid: videoEncoderErroredMid
        };
    }

    function abort() {
        aborted = true;
        encoderRunning = false;
        if (self._silenceMon) { try { self._silenceMon.stop(); } catch (_) {} }
        if (self._ctxMon) { try { self._ctxMon.stop(); } catch (_) {} }
        try { if (videoReader) videoReader.cancel().catch(function(){}); } catch (_) {}
        try { if (audioReader) audioReader.cancel().catch(function(){}); } catch (_) {}
        if (audioTl) { try { audioTl.abort(); } catch (_) {} }
        try { videoEncoder && videoEncoder.close(); } catch (_) {}
        try { audioEncoder && audioEncoder.close(); } catch (_) {}
        // Discard the OPFS partial file: abort() doesn't commit pending
        // bytes; then remove the file outright since the user cancelled.
        if (opfsWritable) {
            try { opfsWritable.abort().catch(function(){}); } catch (_) {}
            opfsWritable = null;
        }
        if (opfsRoot && opfsFileName) {
            try { opfsRoot.removeEntry(opfsFileName).catch(function(){}); } catch (_) {}
        }
    }

    return { init: init, start: start, pause: pause, resume: resume, stop: stop, abort: abort,
        // the moment Stop was pressed: nothing the still-live audio tracks
        // capture after it belongs to the take (see CaptureAudioTimeline.endAt)
        endAudioAt: function (t) { if (audioTl) audioTl.endAt(t); },
        _stats: function () {
            var at = audioTlStats || (audioTl ? audioTl.stats() : null);
            return Object.assign({}, stats, {
                audioClock: audioTl ? "timeline" : "mixer",
                audioSilenceFilledMs: at ? at.gapMs : (stats.audioGapMs || 0),
                audioGapEvents: at ? at.gapEvents : (stats.audioGaps || 0),
                // the take's end read back from the queue at Stop, how long that
                // took, whether the bound cut it, how many sources had gone quiet,
                // and how far behind reading fell (see CaptureAudioTimeline.stop)
                audioDrainedMs: at ? (at.drainedMs || 0) : 0,
                audioDrainMs: at ? (at.drainMs || 0) : 0,
                audioDrainCut: !!(at && at.drainCut),
                audioDrainIdle: at ? (at.drainIdle || 0) : 0,
                audioReadLagMaxMs: at ? (at.readLagMaxMs || 0) : 0,
                videoDomain: timeline.videoDomain, pausedMs: timeline.pausedMs
            });
        } };
}

// ─────────────────────────────────────────────────────────────────────
// MEDIARECORDER FALLBACK
// ─────────────────────────────────────────────────────────────────────

function newMediaRecorderPipeline(self) {
    var mr = null;
    var chunks = [];
    var mime = '';
    var videoTrack = null;
    var hasAudio = false; // hoisted to factory scope so stop() can read it
    var stats = { framesEncoded: 0, audioFrames: 0, bytesOut: 0, queueDepthMax: 0 };

    // Held in closure so start() can rebuild the MediaRecorder with the
    // next-best mime when isTypeSupported() lies (Yandex Browser cohort —
    // TIMBRICA-FRONTEND-4S). The combined MediaStream + bitrate-tuned opts
    // are stream-identity-stable; only the mimeType changes between retries.
    // Built dynamically so the Safari/iOS cohort (no WebCodecs live capture)
    // can record native MP4 instead of WebM (which Safari's MediaRecorder
    // can't produce at all). KEY DIFFERENCE from screen-recorder: for SCREEN
    // capture, Safari's MediaRecorder emits FRAGMENTED MP4 (no moov-at-start,
    // unplayable in Win11 Movies&TV / QuickTime / older VLC) so MP4 was
    // excluded. For CAMERA input, Safari/iOS native MediaRecorder produces a
    // clean, in-order H.264 MP4 with moov-at-start — fully playable as-is, no
    // post-processing needed. So we PREPEND the MP4 tiers when supported; the
    // webm tiers stay as the fallback for Chromium/Firefox (where the
    // WebCodecs path is the primary anyway, and WebM gets Segment Duration
    // patched in via WebmFixDuration below — guarded by `ext === 'webm'`, so
    // clean MP4 output correctly skips that rewrite).
    var preferences = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm'
    ];
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported &&
        MediaRecorder.isTypeSupported('video/mp4;codecs=avc1.42E01E,mp4a.40.2')) {
        preferences = [
            'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
            'video/mp4;codecs=avc1,mp4a.40.2',
            'video/mp4'
        ].concat(preferences);
    }
    var combinedStream = null;
    var mrBaseOpts = null;
    var mimePrefIdx = -1;

    function attachHandlers() {
        mr.ondataavailable = function (e) {
            if (e.data && e.data.size > 0) {
                chunks.push(e.data);
                stats.bytesOut += e.data.size;
                stats.framesEncoded++; // chunks, not frames — but useful as a counter
            }
        };
        mr.onerror = function (e) {
            self._fail(new Error('mediarecorder-runtime: ' + (e.error && e.error.message || 'unknown')));
        };
    }

    function constructMR(idx) {
        mimePrefIdx = idx;
        mime = preferences[idx];
        var opts = Object.assign({}, mrBaseOpts, { mimeType: mime });
        mr = new MediaRecorder(combinedStream, opts);
        attachHandlers();
    }

    async function init(opts) {
        var firstSupported = -1;
        for (var i = 0; i < preferences.length; i++) {
            if (MediaRecorder.isTypeSupported(preferences[i])) { firstSupported = i; break; }
        }
        if (firstSupported === -1) {
            // No preferred MIME supported AND the browser doesn't expose a
            // working default (extreme degraded path). Surface a typed
            // error early instead of letting MediaRecorder.start() throw
            // a generic "error starting the MediaRecorder" message.
            var err = new Error('No supported MediaRecorder mimeType available on this browser');
            err.code = 'MEDIARECORDER_CODEC_UNSUPPORTED';
            err.expected = true;
            throw err;
        }

        // Build mixed audio + video stream the same way the original blade
        // did — kept here so MediaRecorder behaves exactly as before, only
        // the post-processing differs.
        var displayStream = opts.displayStream;
        var micStream = opts.micStream;
        videoTrack = displayStream.getVideoTracks()[0];
        var tracks = [videoTrack];
        var displayAudio = displayStream.getAudioTracks();
        hasAudio = displayAudio.length > 0 || !!micStream;

        if (hasAudio) {
            self._mixer = buildMixer(displayStream, micStream, opts);
            self._mixer.dest.stream.getAudioTracks().forEach(function (t) { tracks.push(t); });
        }
        combinedStream = new MediaStream(tracks);

        // Bitrate hint — MediaRecorder only honours videoBitsPerSecond,
        // CRF semantics aren't available. Use the same QUALITY mapping at
        // 1080p baseline.
        var width = 1920, height = 1080;
        try {
            var s = videoTrack.getSettings();
            if (s.width) width = s.width;
            if (s.height) height = s.height;
        } catch (_) {}
        mrBaseOpts = {
            videoBitsPerSecond: bitrateFor(opts.quality || 'medium', width, height, opts.fps || 30),
        };

        // The first video recorder of a Chromium tab drops its first ~2 s of
        // picture, and a live track needs the warm-up on that very track. This
        // lane runs in Chromium only when WebCodecs could not start; the door
        // skips Firefox (no gap there) — so the wait is ≤ 1.5 s and rare
        // (docs/why/primitives/media-recorder-warm.md).
        if (window.__mediaRecorderWarm) await window.__mediaRecorderWarm({ stream: combinedStream, mimeType: preferences[firstSupported], videoBitsPerSecond: mrBaseOpts.videoBitsPerSecond, maxWaitMs: 1500 });
        try { constructMR(firstSupported); }
        catch (e) { throw new Error('mediarecorder-init: ' + e.message); }

        return {
            width: width, height: height, fps: opts.fps || 30,
            videoCodec: mime, audioCodec: hasAudio ? 'opus' : null,
            hardware: false,
            videoBitrate: mrBaseOpts.videoBitsPerSecond,
            audioBitrate: hasAudio ? 128_000 : 0
        };
    }

    function start() {
        // Guard: if the display track has died between pipeline init and
        // start (user dismissed the share-source tab, OS revoked, etc.),
        // every codec retry below will hit the same dead source. Surface
        // the real cause as a typed SHARING_ENDED error instead of letting
        // the cascade exhaust into the misleading "browser can't record
        // any codec" message — same orchestrator-level check that catches
        // the WebCodecs path. Closes Sentry TIMBRICA-FRONTEND-7J cohort
        // where telemetry showed pipeline:webcodecs → 15s gap →
        // display-track-already-ended → MR.start cascade exhausting on
        // dead track.
        if (!videoTrack || videoTrack.readyState === 'ended') {
            var se = new Error('Camera stopped before recording could start');
            se.code = 'CAMERA_ENDED';
            se.userFacing = true;
            se.expected = true;
            throw se;
        }
        // Yandex Browser (and a few older Android stocks) lie in
        // MediaRecorder.isTypeSupported() — claim VP9/VP8 are supported,
        // construct accepts the mime, but start() throws NotSupportedError
        // because the underlying encoder isn't actually available. Walk
        // the preference list past the lying entry and retry. Surfaced
        // 2026-05-13..27 as TIMBRICA-FRONTEND-4S (2 users, Yandex 26 +
        // older Yandex builds).
        for (;;) {
            try {
                mr.start(1000);
                // Wire silence + AudioContext monitors AFTER mr.start succeeds —
                // closes the MediaRecorder-cohort gap (Дмитрий #738 was open for
                // every non-WebCodecs user until this). isRunning predicate is
                // MR-specific: poll while the recorder is not inactive.
                attachSilenceMonitors(self, function () { return mr && mr.state !== 'inactive'; });
                return;
            }
            catch (e) {
                var isYandexLieSymptom = (e && (e.name === 'NotSupportedError' || (e.message || '').indexOf('start') !== -1));
                if (!isYandexLieSymptom) throw e;
                var nextIdx = -1;
                for (var i = mimePrefIdx + 1; i < preferences.length; i++) {
                    if (MediaRecorder.isTypeSupported(preferences[i])) { nextIdx = i; break; }
                }
                if (nextIdx === -1) {
                    // Exhausted fallbacks — the browser's MediaRecorder is
                    // not functional for any of our supported mimes. Surface
                    // a typed error so the higher tier can show an actionable
                    // toast ("try another browser") rather than the cryptic
                    // built-in NotSupportedError text.
                    var err = new Error('MediaRecorder.start failed for every supported mimeType (' + preferences.join(', ') + ')');
                    err.code = 'MEDIARECORDER_RUNTIME_UNSUPPORTED';
                    err.userFacing = true;
                    // expected:true is the project-wide contract for
                    // "predictable hardware/feature-incompat condition —
                    // the throw site is contracted to surface a user-facing
                    // toast; Sentry's beforeSend drops it". Same shape as
                    // __safeAudioBuffer / __safeWebGL. Closes Sentry
                    // TIMBRICA-FRONTEND-7J (Chrome 148 on Windows — same
                    // browser-lie pattern that originally surfaced as 4S
                    // on Yandex). userFacing stays — it's a separate
                    // contract that lets the caller blit err.message
                    // verbatim into a toast where appropriate.
                    err.expected = true;
                    err.cause = e;
                    throw err;
                }
                try { constructMR(nextIdx); } catch (e2) { throw e; /* surface original */ }
            }
        }
    }
    function pause() { try { mr.pause(); } catch (_) {} }
    function resume() { try { mr.resume(); } catch (_) {} }

    // Settles on the first sign the recorder is done (recorder-stop.js):
    // Firefox fires `error` — or nothing — instead of `stop` when the tracks
    // end around mr.stop(). That hung the screen recorder until 09.07.2026;
    // this clone of it never got the fix. A non-`stop` settle salvages the
    // chunks gathered so far.
    function stop() {
        return settleMr(mr, 8000).then(function (via) {
            if (via !== 'stop') self._tel('record_mr_stop_salvaged', { via: via, chunks: chunks.length, state: mr.state });
            return buildMrResult();
        });
    }
    async function buildMrResult() {
        var rawType = mr.mimeType || mime || 'video/webm';
        var rawBlob = new Blob(chunks, { type: rawType });
        var ext = rawType.indexOf('mp4') !== -1 ? 'mp4' : 'webm';

        // Same loud-fail guard as WebCodecs path. If MediaRecorder
        // produced zero data chunks the share source died before
        // the first ondataavailable fired. User expects a file;
        // returning an empty blob silently breaks trust.
        if (chunks.length === 0 || rawBlob.size < 256) {
            var err = new Error('recording-empty-no-data');
            err.userFacing = true;
            throw err;
        }

        // Post-process WebM to inject Segment Duration. Skipped for
        // MP4 (would require full re-mux) — but we only emit WebM
        // in this pipeline so this branch always runs. Bounded: an
        // unpatched file still plays; a patch that never returns is no file.
        var finalBlob = rawBlob;
        if (ext === 'webm' && window.WebmFixDuration) {
            var fixed = await bounded(window.WebmFixDuration.patch(rawBlob), 15000);
            if (fixed.ok && fixed.value) finalBlob = fixed.value;
        }
        // Tear down the silence/ctx monitors + surface the same audio
        // flags the WebCodecs path returns, so the post-stop banner
        // cascade treats MR results identically.
        if (self._silenceMon) { try { self._silenceMon.stop(); } catch (_) {} }
        if (self._ctxMon) { try { self._ctxMon.stop(); } catch (_) {} }
        return {
            blob: finalBlob,
            mime: rawType,
            ext: ext,
            stats: stats,
            audioMissing: !!(self._userWantedAudio && !hasAudio),
            audioDroppedMid: !!self._audioDroppedMid,
            audioSilenceMid: !!self._audioSilenceMid,
            audioContextSuspended: !!self._audioContextSuspended
        };
    }
    function abort() {
        if (self._silenceMon) { try { self._silenceMon.stop(); } catch (_) {} }
        if (self._ctxMon) { try { self._ctxMon.stop(); } catch (_) {} }
        try { mr && mr.state !== 'inactive' && mr.stop(); } catch (_) {}
        chunks = [];
    }
    return { init: init, start: start, pause: pause, resume: resume, stop: stop, abort: abort, _stats: function(){return stats;} };
}

// ─────────────────────────────────────────────────────────────────────
// ORCHESTRATOR
// ─────────────────────────────────────────────────────────────────────

function WebcamRecorder(opts) {
    this._opts = opts || {};
    this._state = 'idle';
    this._pipeline = null;
    this._displayStream = null;
    this._micStream = null;
    this._mixer = null;
    this._timerStart = 0;
    this._pausedTotal = 0;
    this._lastPauseAt = 0;
    this._micManager = null;
}

WebcamRecorder.prototype.getCapabilities = function () {
    return {
        getDisplayMedia: hasGetDisplayMedia(),
        webcodecs: hasWebCodecsLive(),
        mediaRecorder: hasMediaRecorder(),
        fileSystemAccess: hasFileSystemAccess()
    };
};

WebcamRecorder.prototype._setState = function (s) {
    this._state = s;
    if (this._opts.onState) try { this._opts.onState(s); } catch (_) {}
};

WebcamRecorder.prototype._tel = function (name, payload) {
    if (this._opts.telemetry) try { this._opts.telemetry(name, payload || {}); } catch (_) {}
};

WebcamRecorder.prototype._fail = function (err) {
    if (this._state === 'error') return;
    this._setState('error');
    var msg = (err && err.message || '').slice(0, 200);
    var code = (err && (err.code || err.message) || '').slice(0, 60);
    this._tel('record_error', {
        msg: msg,
        code: code,
        userFacing: !!(err && err.userFacing),
        // Snapshot encoder + state context for diagnosis. With these we
        // can tell whether failures cluster around 0-frame, OOM-on-flush,
        // muxer-finalize, etc., without needing a repro.
        pipeline: this._pipelineKind || '',
        // which bounded step of a Stop the failure came from, and how each went
        stopPhase: this._stopPhase || '',
        stopSteps: (this._stopPhases || []).map(function (s) { return s.p + ':' + s.ms + (s.to ? '!' : ''); }).join(','),
        durMs: this._timerStart ? Math.round(performance.now() - this._timerStart - this._pausedTotal) : 0,
        framesEncoded: (this._pipeline && this._pipeline._stats && this._pipeline._stats().framesEncoded) || 0
    });
    if (this._opts.onError) try { this._opts.onError(err); } catch (_) {}
};

WebcamRecorder.prototype.start = async function () {
    if (this._state !== 'idle' && this._state !== 'done' && this._state !== 'error') {
        return false;
    }
    this._setState('starting');
    this._stopPhases = null; this._stopPhase = '';   // a new take: no Stop yet
    this._stopAtMs = null;
    this._tel('record_start_request', { quality: this._opts.quality, fps: this._opts.fps,
        captureMic: !!this._opts.captureMic, captureSysAudio: !!this._opts.captureSysAudio });
    // Capture user intent ONCE — pipeline.stop() reads this back to know
    // whether to flag the result as audioMissing. Computed here (not in
    // pipeline.init) because the pipeline factory closes over a snapshot
    // of opts; reading from this._opts inside stop() risks staleness if
    // the orchestrator is reused. Webcam has no system audio — mic only.
    this._userWantedAudio = !!this._opts.captureMic;

    // Request PERSISTENT storage so the browser won't evict our OPFS
    // recording file mid-capture. Closes opfs-writable-close "A requested
    // file or directory could not be found" (4/7d, ALL Yandex, 8–17 min
    // recordings): Yandex aggressively evicts best-effort OPFS under storage
    // pressure, so a long recording's file vanished between createWritable()
    // and close(). persist() upgrades the origin to "persistent" — the UA
    // must NOT evict it without explicit user action. Best-effort + fire-
    // and-forget (the Record click is a user gesture, which is when grants
    // are most likely); recording proceeds regardless of the outcome.
    try {
        if (navigator.storage && typeof navigator.storage.persist === 'function') {
            var self0 = this;
            navigator.storage.persisted().then(function (already) {
                if (already) { self0._tel('record_storage_persist', { state: 'already' }); return; }
                return navigator.storage.persist().then(function (granted) {
                    self0._tel('record_storage_persist', { state: granted ? 'granted' : 'denied' });
                });
            }).catch(function () {});
        }
    } catch (_) {}

    var caps = this.getCapabilities();
    this._tel('record_capability', caps);

    // Disk-space precheck. If OPFS quota is very low, warn the user that
    // a long recording will fill it. Doesn't block start — user can still
    // record short clips on tight quota — but visibility into the limit
    // is the etalon answer instead of letting the encoder die mid-record.
    try {
        var quotaProbe = await probeStorageQuota();
        // PRE-FLIGHT AUTO-EVICT: closes the 8f56cfd3 today-cluster (3 lost
        // recordings on the same session because OPFS was already nearly
        // full and autoEvictIfNeeded only runs AFTER successful save). When
        // free space is below 1 GB OR usage above 70%, drop oldest history
        // entries until we have headroom for a normal recording. Failed
        // recordings now also delete their partial OPFS file in pipeline
        // stop() (see above), so quota only ever shrinks via real history
        // not via orphaned partials.
        if (quotaProbe.supported &&
            (quotaProbe.free < 1024 * 1024 * 1024 || quotaProbe.usagePct > 0.7) &&
            window.RecordingsHistory && typeof window.RecordingsHistory.autoEvictIfNeeded === 'function') {
            try {
                // Aggressive: trigger at 60%, evict down to 40% — frees the
                // most headroom in one pass; user can record many more clips
                // before next evict.
                var evictResult = await window.RecordingsHistory.autoEvictIfNeeded(60, 40);
                if (evictResult && evictResult.evicted > 0) {
                    this._tel('record_history_preflight_evicted', {
                        count: evictResult.evicted,
                        freedBytes: evictResult.freed,
                        reason: quotaProbe.free < 1024 * 1024 * 1024 ? 'low_free' : 'high_usage'
                    });
                    // Re-probe so the warning below uses the freshly evicted state.
                    quotaProbe = await probeStorageQuota();
                }
            } catch (_) {}
        }
        if (quotaProbe.supported && quotaProbe.free < 500 * 1024 * 1024) {
            this._tel('record_quota_low_at_start', {
                freeMb: Math.round(quotaProbe.free / 1024 / 1024),
                quotaMb: Math.round(quotaProbe.quota / 1024 / 1024)
            });
            if (this._opts.onWarning) {
                try { this._opts.onWarning('quota_low_at_start', quotaProbe); } catch (_) {}
            }
        }
        this._initialQuota = quotaProbe;
    } catch (_) {}

    var fps = this._opts.fps || 30;

    // Acquire camera (+ mic) via the blade-supplied acquire() callback. The
    // blade owns CameraManager / device selection / facingMode / the
    // permission prompt, and returns { videoStream, micStream }. This keeps
    // all camera-specific UX out of the source-agnostic recorder engine.
    var streams;
    try {
        streams = await this._opts.acquire();   // blade returns { videoStream, micStream }
    } catch (e) {
        // CameraManager throws typed errors (e.code: denied|not_found|busy|unresponsive|not_supported|generic).
        this._tel('record_camera_denied', { code: (e && e.code) || '', msg: (e && e.message || '').slice(0, 100) });
        if (this._opts.onCameraError) { try { this._opts.onCameraError(e); } catch (_) {} }
        this._setState('idle');
        return false;
    }
    // The video-only camera MediaStream takes the slot the screen-capture
    // stream used to (pipelines read .getVideoTracks()[0]); mic is separate.
    this._displayStream = streams && streams.videoStream;
    this._micStream = (streams && streams.micStream) || null;
    if (!this._displayStream || !this._displayStream.getVideoTracks()[0]) {
        var ne = new Error('no-camera-track'); ne.code = 'not_found'; ne.userFacing = true;
        this._tel('record_camera_denied', { code: 'no_track' });
        if (this._opts.onCameraError) { try { this._opts.onCameraError(ne); } catch (_) {} }
        this._setState('idle'); return false;
    }
    this._tel('record_start_ok', { wantedMic: !!this._opts.captureMic, gotMic: !!this._micStream });

    // Mid-recording mic-disappeared hooks — bound to the camera mic track.
    // (USB device unplugged, headset disconnected, OS revoked permission,
    // OS/exclusive-app mute.) Pre-fix the audio reader would hit done=true,
    // audio loop exits silent, muxer finalizes with partial audio; user has
    // no idea why the file's second half is silent. Now: log telemetry + flag
    // audioDroppedMid so the post-recording banner explains, and route
    // mute/unmute through the shared silenceDispatch.
    var self = this;
    if (this._opts.captureMic && this._micStream) {
        var micTrack = this._micStream.getAudioTracks()[0];
        if (micTrack) {
            micTrack.addEventListener('ended', function () {
                if (self._state !== 'recording' && self._state !== 'paused') return;
                self._tel('record_mic_disappeared_mid', {
                    durMs: self._timerStart ? Math.round(performance.now() - self._timerStart - self._pausedTotal) : 0
                });
                self._audioDroppedMid = true;
            });
            micTrack.addEventListener('mute', function () { onTrackMute(self, 'mic_track_mute'); });
            micTrack.addEventListener('unmute', function () {
                if (self._state !== 'recording' && self._state !== 'paused') return;
                if (self._opts.onWarning) { try { self._opts.onWarning('audio_silence_recovered', {}); } catch (_) {} }
            });
        }
    }

    // Camera video-track onended — fires when the camera is unplugged /
    // released / the device track ends mid-record. Auto-stop so the user
    // gets the partial recording rather than a stuck UI.
    var vTrack = this._displayStream.getVideoTracks()[0];
    if (vTrack) {
        vTrack.onended = function () {
            if (self._state === 'recording' || self._state === 'paused') {
                self.stop();
            }
        };
    }

    // === MIC-MISSING PRE-CHECK (mic-only adaptation of Кот #534) ===============
    // User asked for mic but acquire() came back without one (denied, no input
    // device, busy). Detect the mismatch HERE — before the encoder spins up —
    // and call onAudioMissing so the blade can show a blocking confirm modal.
    // User can cancel and re-try, or knowingly continue recording silent video.
    if (this._opts.captureMic && !this._micStream && this._opts.onAudioMissing) {
        this._tel('record_audio_missing_prestart', { wantMic: true, gotMic: false, missing: 'mic' });
        var decision = 'continue';
        try { decision = await this._opts.onAudioMissing({ wantMic: true, gotMic: false, missing: ['mic'] }); } catch (_) { decision = 'continue'; }
        if (decision === 'cancel') {
            this._tel('record_audio_missing_cancelled', { missing: 'mic' });
            try { this._displayStream.getTracks().forEach(function (t) { t.stop(); }); } catch (_) {}
            this._displayStream = null;
            this._setState('idle'); return false;
        }
        this._tel('record_audio_missing_continued', { missing: 'mic' });
    }

    // Pick pipeline.
    var useWebCodecs = caps.webcodecs && window.Mp4Muxer;
    // Sweep stale OPFS recordings before starting fresh. Catches every
    // flavor of leftover: previous-recording file orphaned because the
    // blade resets `rec=null` on "New recording" before our cleanup ran;
    // orphans from a prior tab whose pagehide handler was killed; files
    // left when the user pressed Record again without going through New.
    // Idempotent. The user has either revoked the previous preview URL
    // (newBtn click → URL.revokeObjectURL) or this is page-load time, so
    // we are not pulling bytes out from under any in-use Blob.
    try { await sweepStaleOpfsRecordings(); } catch (_) {}
    this._opfsRoot = null;
    this._opfsFileName = null;

    var pipelineOpts = {
        displayStream: this._displayStream,
        micStream: this._micStream,
        fps: fps,
        quality: this._opts.quality || 'medium',
        micVolume: this._opts.micVolume,
        sysVolume: this._opts.sysVolume
    };

    var pipelineKind = '';
    var encoderInfo = null;
    // ── Display-track health check, shared between paths ──
    // If the display track is already 'ended' by the time we're about to
    // init a pipeline, NO pipeline will succeed — both WebCodecs and
    // MediaRecorder need a live source. Surfacing this distinction (vs
    // letting WebCodecs throw, MR also throw, and the user see a generic
    // "browser can't record" toast) closes Sentry TIMBRICA-FRONTEND-7J
    // (Yandex Browser 26 on Chromium 148 — telemetry showed
    // pipeline:webcodecs chosen → 15 s later record_visibility_hidden →
    // record_webcodecs_failed_fallback:"display-track-already-ended" →
    // MR.start failed for every supported mimeType. User had stopped
    // sharing mid-init; the cryptic codec error mis-attributed it as a
    // browser incompat). Reused here as a helper because both pipeline
    // catches use it.
    function checkSharingEnded(rawErr) {
        var vTrack = null;
        try { vTrack = self._displayStream && self._displayStream.getVideoTracks()[0]; } catch (_) {}
        var msg = (rawErr && rawErr.message) || '';
        var trackDead = !vTrack || vTrack.readyState === 'ended';
        if (msg === 'display-track-already-ended' || trackDead) {
            var se = new Error('Camera stopped before recording could start');
            se.code = 'CAMERA_ENDED';
            se.userFacing = true;
            se.expected = true;  // not an app bug — user released the camera
            se.cause = rawErr;
            return se;
        }
        return null;
    }

    if (useWebCodecs) {
        try {
            this._pipeline = newWebCodecsPipeline(this);
            encoderInfo = await this._pipeline.init(pipelineOpts);
            pipelineKind = 'webcodecs';
        } catch (e) {
            try { this._pipeline.abort(); } catch (_) {}
            this._pipeline = null;
            this._tel('record_webcodecs_failed_fallback', { msg: (e && e.message || '').slice(0, 100) });
            // Short-circuit: if the display track died, MR will fail with
            // the exact same dead source — don't waste the round-trip and
            // don't show the user a misleading "browser can't record" toast.
            var sharingEnded = checkSharingEnded(e);
            if (sharingEnded) {
                this._tel('record_sharing_ended', { from: 'webcodecs_init' });
                this._fail(sharingEnded);
                this._cleanupStreams();
                return false;
            }
            // fall through to MR fallback (genuine WebCodecs codec-config
            // failure — e.g. no-h264-encoder / no-aac-encoder)
        }
    }
    if (!this._pipeline && caps.mediaRecorder) {
        // Re-check before paying the MR init cost — between WebCodecs
        // bailout and here, the user may have stopped sharing.
        var preInitDeath = checkSharingEnded(null);
        if (preInitDeath) {
            this._tel('record_sharing_ended', { from: 'pre_mr_init' });
            this._fail(preInitDeath);
            this._cleanupStreams();
            return false;
        }
        try {
            this._pipeline = newMediaRecorderPipeline(this);
            encoderInfo = await this._pipeline.init(pipelineOpts);
            pipelineKind = 'mediarecorder';
        } catch (e) {
            var sharingEndedMR = checkSharingEnded(e);
            if (sharingEndedMR) {
                this._tel('record_sharing_ended', { from: 'mr_init' });
                this._fail(sharingEndedMR);
                this._cleanupStreams();
                return false;
            }
            this._fail(e);
            this._cleanupStreams();
            return false;
        }
    }
    if (!this._pipeline) {
        this._fail(new Error('no-pipeline-available'));
        this._cleanupStreams();
        return false;
    }

    this._encoderInfo = encoderInfo;
    this._pipelineKind = pipelineKind;

    // Storage mode is set by WebCodecs init(); MR fallback is always 'memory'.
    var storageMode = this._storageMode || 'memory';
    this._tel('record_pipeline_chosen', Object.assign({ pipeline: pipelineKind, storageMode: storageMode, displaySurface: this._displaySurface || '' }, encoderInfo || {}));
    this._tel('record_storage_chosen', { kind: storageMode });

    // Memory-storage long-recording warning. Closes the 2/7d
    // `muxer-finalize: Array buffer allocation failed` cluster on browsers
    // without OPFS (2 users lost 1h+ recordings — sid 40374ec8 lost a 2h37m
    // recording, sid 0fc75e74 lost 1h01m). The OPFS path streams chunks to
    // disk and can handle 10h+; the memory path accumulates in JS heap and
    // OOMs at ~30-60min depending on quality. Telling the user upfront beats
    // letting them film for 2 hours then losing it.
    if (storageMode === 'memory') {
        this._tel('record_memory_storage_warning', { pipeline: pipelineKind });
        if (this._opts.onWarning) {
            try { this._opts.onWarning('memory_storage_long_recording_risk', {}); } catch (_) {}
        }
    }

    // NO pagehide deletion — REMOVED (feedback #939, ported from screen-recorder
    // recorder.js:2166). The old handler deleted `_opfsFileName` on every
    // pagehide, which destroyed the user's recording on any tab close / reload /
    // accidental navigation MID-RECORDING even though every byte was already
    // streamed to OPFS — the exact "ушёл со страницы → запись пропала" data-loss
    // this whole change is about. screen-recorder hit this and removed it; webcam
    // carried the destroyer. Cleanup of true garbage is owned by the same three
    // paths screen-recorder relies on: pipeline abort() (deletes its partial
    // synchronously on real failure), the age-gated sweepStaleOpfsRecordings on
    // next start, and history reconcile() adoption, which now skips 0-byte stubs
    // and flags interrupted MP4s instead of destroying surviving partials.

    this._timerStart = performance.now();
    this._pausedTotal = 0;
    this._pausedTotalUs = 0;   // seamless-pause offset, grows on each resume
    this._resumeSeq = 0;
    this._audioRebaseOk = undefined;  // re-probed per recording (audioLoop)
    // Seamless pause re-stamps frame timestamps; it only works when the video
    // source is a real-time camera track. A canvas captureStream (used for
    // aspect-crop / blur) has a non-wall-clock timeline that desyncs A/V under
    // re-stamping, so the caller disables it via opts.seamlessPause=false →
    // pause then falls back to a consistent freeze-gap (still A/V-synced).
    this._seamlessOk = (this._opts.seamlessPause !== false);
    this._pipeline.start();
    this._setState('recording');

    // Wake Lock — prevent the screen from sleeping while recording.
    // Without this, a 2-hour lecture turns into a 25-min recording when
    // OS power-management dims the display and Chrome reclaims the
    // hardware encoder (telemetry yesterday: 4 events of "Codec reclaimed
    // due to inactivity" on tabs that backgrounded). Best-effort: on
    // refusal we still record, just less robustly.
    var self3 = this;
    if (typeof navigator !== 'undefined' && navigator.wakeLock && typeof navigator.wakeLock.request === 'function') {
        // Skip the initial request if the document is already hidden — common
        // case: user clicked record and immediately switched to the share-
        // source tab. The WakeLock API rejects with "The requesting page is
        // not visible" in that state (564/575 wake_lock_failed events fit
        // this pattern in 7d telemetry). The visibilitychange listener below
        // re-acquires automatically when the page becomes visible again, so
        // recording is unaffected — but we'd otherwise pollute telemetry with
        // ~half of all recordings looking like "wake lock broken".
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            self3._tel('record_wake_lock_deferred', { reason: 'document_hidden' });
        } else {
            navigator.wakeLock.request('screen').then(function (sentinel) {
                self3._wakeLock = sentinel;
                self3._tel('record_wake_lock_acquired', {});
                sentinel.addEventListener('release', function () {
                    // System released the lock (e.g., tab lost visibility).
                    // Re-request when we come back into foreground — handled
                    // below in the visibilitychange listener.
                    self3._wakeLock = null;
                });
            }).catch(function (e) {
                var msg = (e && e.message || '').slice(0, 80);
                // Same "page is not visible" filter — between request() and
                // its promise rejecting, the page may have backgrounded.
                // The visibilitychange listener will re-acquire when visible.
                if (/page is not visible/i.test(msg)) {
                    self3._tel('record_wake_lock_deferred', { reason: 'visibility_race', msg: msg });
                } else {
                    self3._tel('record_wake_lock_failed', { msg: msg });
                }
            });
        }
    }

    // Visibility-warning + wake-lock re-acquire on tab back-to-foreground.
    // Browsers may pause encode work + release wake lock when tab is hidden.
    if (!this._visibilityWired) {
        this._visibilityWired = true;
        var self4 = this;
        document.addEventListener('visibilitychange', function () {
            if (self4._state !== 'recording' && self4._state !== 'paused') return;
            if (document.visibilityState === 'hidden') {
                self4._tel('record_visibility_hidden', {});
                if (self4._opts.onWarning) {
                    try { self4._opts.onWarning('tab_backgrounded', {}); } catch (_) {}
                }
            } else if (document.visibilityState === 'visible' && !self4._wakeLock &&
                       navigator.wakeLock && typeof navigator.wakeLock.request === 'function') {
                navigator.wakeLock.request('screen').then(function (s) {
                    self4._wakeLock = s;
                    self4._tel('record_wake_lock_reacquired', {});
                }).catch(function () {});
            }
        });
    }

    // Periodic quota check (~every 30 s) while recording. When usage crosses
    // 80% of quota, surface a warning so the user can stop deliberately
    // before write() throws QuotaExceededError mid-frame.
    if (this._quotaInterval) { try { clearInterval(this._quotaInterval); } catch (_) {} }
    var self5 = this;
    var warnedCritical = false;
    this._quotaInterval = setInterval(async function () {
        if (self5._state !== 'recording' && self5._state !== 'paused') return;
        try {
            var p = await probeStorageQuota();
            if (!p.supported) return;
            if (p.usagePct >= 0.95 && !warnedCritical) {
                // 95%: extremely close to quota — recommend immediate stop.
                warnedCritical = true;
                self5._tel('record_quota_critical', {
                    freeMb: Math.round(p.free / 1024 / 1024),
                    usagePct: Math.round(p.usagePct * 100)
                });
                if (self5._opts.onWarning) {
                    try { self5._opts.onWarning('quota_critical', p); } catch (_) {}
                }
            } else if (p.usagePct >= 0.8 && !self5._quotaWarned) {
                self5._quotaWarned = true;
                self5._tel('record_quota_low', {
                    freeMb: Math.round(p.free / 1024 / 1024),
                    usagePct: Math.round(p.usagePct * 100)
                });
                if (self5._opts.onWarning) {
                    try { self5._opts.onWarning('quota_low', p); } catch (_) {}
                }
            }
        } catch (_) {}
    }, 30_000);

    return true;
};

WebcamRecorder.prototype.pause = function () {
    if (this._state !== 'recording') return;
    this._lastPauseAt = performance.now();
    this._pipeline.pause();
    this._setState('paused');
    this._tel('record_pause', {});
};

WebcamRecorder.prototype.resume = function () {
    if (this._state !== 'paused') return;
    this._pausedTotal += performance.now() - this._lastPauseAt;
    // Shared seamless-pause offset (µs) — set BEFORE state flips to 'recording'
    // so both the video and audio encode loops read the same value with no
    // race. Each loop subtracts it from frame timestamps to drop the paused
    // span from the output timeline (no freeze-gap). Wall-clock paused time ≈
    // the source-timestamp gap, so video+audio shift together → stay in sync.
    this._pausedTotalUs = Math.round(this._pausedTotal * 1000);
    this._resumeSeq = (this._resumeSeq || 0) + 1;   // audio gap-fill exempts the jump across a pause
    this._pipeline.resume();
    this._setState('recording');
    this._tel('record_resume', {});
};

WebcamRecorder.prototype.stop = async function () {
    if (this._state !== 'recording' && this._state !== 'paused') return null;
    // Re-entry guard. Without this, browser "Stop sharing" + the in-app
    // stop button can both fire stop() — track.onended races the click —
    // and the second invocation tries to call pipeline.stop() on an
    // already-torn-down encoder, throwing.
    if (this._stopInFlight) return this._stopInFlight;
    var self = this;
    this._stopInFlight = (async function () {
        self._setState('finalizing');
        var stopAt = performance.now();
        // the take ends here: the audio timeline reads what its still-live
        // tracks captured before it, and nothing after it
        self._stopAtMs = stopAt;
        if (self._pipeline && typeof self._pipeline.endAudioAt === 'function') self._pipeline.endAudioAt(stopAt);
        var durMs = Math.round(stopAt - self._timerStart - self._pausedTotal);
        self._tel('record_stop', { durMs: durMs });

        // Release wake lock + stop quota monitor before the (potentially
        // slow) finalize, so the user's screen can sleep again the moment
        // they hit Stop.
        self._releaseWakeLock();

        var result = null;
        try { result = await self._pipeline.stop(); }
        catch (e) {
            // Propagate userFacing flag so the blade can show a specific
            // message instead of the generic "couldn't save" toast.
            var wrapped = new Error('pipeline-stop: ' + e.message);
            wrapped.userFacing = !!e.userFacing;
            wrapped.code = e.message; // raw code for telemetry
            self._fail(wrapped);
            self._cleanupStreams();
            return null;
        }
        self._cleanupStreams();

        if (!result) {
            // Should be unreachable now — pipeline.stop() always either
            // returns a result or throws — but keep the guard.
            var err = new Error('pipeline-no-output');
            err.userFacing = true;
            self._fail(err);
            return null;
        }

        self._setState('done');
        var stats = self._pipeline._stats ? self._pipeline._stats() : {};
        self._tel('record_finalize_ok', {
            pipeline: self._pipelineKind,
            ext: result.ext, mime: result.mime,
            sizeBytes: result.blob.size,
            durMs: durMs,
            framesEncoded: stats.framesEncoded || 0,
            // the source's real rate: a take under 2 fps is a frozen source
            // (the composite class of 2026-09-23), not a slow encoder
            effFps: durMs > 0 ? Math.round((stats.framesEncoded || 0) / (durMs / 1000) * 10) / 10 : 0,
            audioFrames: stats.audioFrames || 0,
            queueDepthMax: stats.queueDepthMax || 0,
            audioGaps: stats.audioGaps || 0,
            audioGapMs: stats.audioGapMs || 0,
            audioGapsUnfilled: stats.audioGapsUnfilled || 0,
            audioClock: stats.audioClock || 'mixer',
            audioSilenceFilledMs: stats.audioSilenceFilledMs || 0,
            audioGapEvents: stats.audioGapEvents || 0,
            stopSteps: (self._stopPhases || []).map(function (s) { return s.p + ':' + s.ms + (s.to ? '!' : ''); }).join(','),
            audioDrainedMs: stats.audioDrainedMs || 0,
            audioDrainMs: stats.audioDrainMs || 0,
            audioDrainCut: stats.audioDrainCut ? 1 : 0,
            audioDrainIdle: stats.audioDrainIdle || 0,
            audioReadLagMaxMs: stats.audioReadLagMaxMs || 0,
            videoDomain: stats.videoDomain || ''
        });

        var enriched = Object.assign({}, result, {
            durationMs: durMs,
            pipeline: self._pipelineKind,
            codec: self._encoderInfo && self._encoderInfo.videoCodec
        });

        // Patch the actual recording duration into the IDB sidecar — the
        // pipeline only knew its own internal time; orchestrator has the
        // real wall-clock-minus-paused value. Fire-and-forget; failure
        // here doesn't matter (durMs is a nice-to-have for the history UI).
        if (result.opfsFileName && window.RecordingsHistory && typeof window.RecordingsHistory.save === 'function') {
            window.RecordingsHistory.save({
                fileName: result.opfsFileName,
                createdAt: parseInt(result.opfsFileName.match(new RegExp('_' + TOOL_SLUG + '_(\\d+)_'))[1], 10) || Date.now(),
                durMs: durMs,
                sizeBytes: result.blob.size,
                mime: result.mime,
                ext: result.ext,
                audioMissing: !!result.audioMissing,
                audioDroppedMid: !!result.audioDroppedMid,
                quotaExceededMid: !!result.quotaExceededMid,
                label: null
            }).catch(function () {});
            // Quota-pressure auto-evict: when storage crosses 80%, drop
            // the oldest recordings until under 70%. Runs in background
            // so it doesn't delay onResult/preview.
            if (typeof window.RecordingsHistory.autoEvictIfNeeded === 'function') {
                window.RecordingsHistory.autoEvictIfNeeded().then(function (r) {
                    if (r && r.evicted > 0) {
                        self._tel('record_history_auto_evicted', { count: r.evicted, freedBytes: r.freed });
                    }
                }).catch(function () {});
            }
        }

        // CRITICAL: emit result via callback. This is the only way the
        // caller hears about results from the AUTO-STOP path (where the
        // browser's "Stop sharing" button fires track.onended → our own
        // stop() runs internally — the caller never gets to await it).
        // Without this callback, the UI stays stuck in 'finalizing' state
        // even though the file is fully ready in memory.
        if (self._opts.onResult) {
            try { self._opts.onResult(enriched); } catch (_) {}
        }
        return enriched;
    })();
    return this._stopInFlight;
};

WebcamRecorder.prototype.cancel = function () {
    if (this._state === 'idle' || this._state === 'done') return;
    this._tel('record_cancel', { state: this._state });
    if (this._pipeline) try { this._pipeline.abort(); } catch (_) {}
    this._releaseWakeLock();
    this._cleanupStreams();
    this._stopInFlight = null;
    this._setState('idle');
};

WebcamRecorder.prototype._releaseWakeLock = function () {
    if (this._wakeLock) {
        try { this._wakeLock.release().catch(function(){}); } catch (_) {}
        this._wakeLock = null;
        this._tel('record_wake_lock_released', {});
    }
    if (this._quotaInterval) {
        try { clearInterval(this._quotaInterval); } catch (_) {}
        this._quotaInterval = null;
    }
};

WebcamRecorder.prototype._cleanupStreams = function () {
    if (this._displayStream) {
        try { this._displayStream.getTracks().forEach(function (t) { t.stop(); }); } catch (_) {}
        this._displayStream = null;
    }
    if (this._micStream) {
        if (this._micManager) try { this._micManager.stop(); } catch (_) {}
        else try { this._micStream.getTracks().forEach(function (t) { t.stop(); }); } catch (_) {}
        this._micStream = null;
        this._micManager = null;
    }
    if (this._mixer && this._mixer.ctx) {
        try { this._mixer.ctx.close().catch(function () {}); } catch (_) {}
    }
    this._mixer = null;
};

// Expose volume setters so the UI sliders update mixer in real time
// without rebuilding the pipeline.
WebcamRecorder.prototype.setMicVolume = function (v) {
    if (this._mixer && this._mixer.micGain) this._mixer.micGain.gain.value = v;
};
WebcamRecorder.prototype.setSysVolume = function (v) {
    if (this._mixer && this._mixer.sysGain) this._mixer.sysGain.gain.value = v;
};

// Static helpers — exposed for UI feature-detection / hint rendering.
WebcamRecorder.hasGetDisplayMedia = hasGetDisplayMedia;
WebcamRecorder.hasWebCodecsLive = hasWebCodecsLive;
WebcamRecorder.hasMediaRecorder = hasMediaRecorder;
WebcamRecorder.hasFileSystemAccess = hasFileSystemAccess;

window.WebcamRecorder = WebcamRecorder;

})();
